import {
  type AdoptRequest,
  AdoptResponse,
  AlreadySignedIn,
  type AddLessonExerciseRequest,
  AddLessonExerciseResponse,
  AiCreditsResponse,
  ArchiveListResponse,
  BoardImageResponse,
  BoardSnapshotResponse,
  HomeworkPhotoResponse,
  HomeworkPhotoViewedResponse,
  HomeworkDoneResponse,
  HomeworkAnswerResponse,
  HomeworkAnswerViewedResponse,
  HomeworkMaterialUploadResponse,
  HomeworkSaveAsPlanResponse,
  SourceImagesResponse,
  ExtractJobResponse,
  ExtractLatestResponse,
  type ExtractStartRequest,
  BoardTicketResponse,
  type CancelOccurrenceRequest,
  type CourseTestRequest,
  CourseTestResponse,
  type CreateCallRequest,
  type CreateColleagueReferralRequest,
  type CreateFeedbackNoteRequest,
  type EntryTelemetryEvent,
  maskApiPath,
  type ErrorCode,
  isErrorCode,
  type CreateSeriesRequest,
  type CreateSingleLessonRequest,
  type CreateOpeningsRequest,
  CreateOpeningsResponse,
  type CreateGroupScheduleRequest,
  GroupOccurrenceView,
  GroupScheduleView,
  type UpdateGroupScheduleRequest,
  type MoveOccurrenceRequest,
  type OccurrenceOverrideRequest,
  OccurrenceOverrideView,
  type UpdateSingleLessonRequest,
  ScheduleExceptionView,
  ScheduleSeriesListResponse,
  ScheduleSeriesView,
  type ScheduleSlotInput,
  type UpdateSeriesRequest,
  type UpdateSlotRequest,
  type EndSeriesRequest,
  EndSeriesResponse,
  type CreateHomeworkRequest,
  HomeworkCard,
  type HomeworkBatchEvent,
  HomeworkListResponse,
  UnopenedHomeworkResponse,
  type HomeworkStateFilter,
  HomeworkSummary,
  HomeworkReviewsResponse,
  ReturnReviewsResponse,
  AnswerReviewState,
  type ReturnAnswerRequest,
  type SaveAnswerReviewRequest,
  SaveAnswerReviewResponse,
  type SaveReviewRequest,
  SaveReviewResponse,
  NextOccurrenceResponse,
  type UpdateHomeworkRequest,
  ImportJobStatus,
  MaterialJobsResponse,
  type BoardFilesRequest,
  GuestLinkResponse,
  Lesson,
  type LessonBatchRequest,
  LessonBatchResponse,
  type LessonListPart,
  LessonListResponse,
  LessonPlanLink,
  LessonSignalsResponse,
  MediaBatchResult,
  MediaFile,
  MediaBook,
  MediaFolder,
  MediaLinkResponse,
  MediaListResponse,
  MediaPreviewResponse,
  MediaQuota,
  MediaUsage,
  MediaUsageDigest,
  MeResponse,
  MicResponse,
  PendingUploadListResponse,
  Plan,
  PlanLessonsResponse,
  PlanListResponse,
  PLAN_WRITER_FORMAT,
  PlanAppendsResponse,
  type PlanSources,
  type PlotScope,
  type PlotSpec,
  PlanSummary,
  SavePlanSlidesResponse,
  PlotResponse,
  type SlideSnapshot,
  RedeemInviteResponse,
  type RedeemGuestLinkRequest,
  RedeemGuestLinkResponse,
  StudentLessonLinkResponse,
  RestoreReport,
  RoomRosterResponse,
  ScheduleResponse,
  SessionResponse,
  type Attendance,
  type ContactInput,
  type OccurrenceKey,
  SettlePaymentsResponse,
  StudentContactResponse,
  StudentDetail,
  PairOccurrencesResponse,
  type PairOccurrenceFilter,
  type UpdateStudentProfileRequest,
  type UpdateTrackingRequest,
  type Subject,
  StudentListResponse,
  StudentWithInvite,
  type TelemetryBatch,
  TokenResponse,
  TrashBatchResult,
  TrashGroupContents,
  TrashListResponse,
  UploadFingerprint,
  UploadSession,
} from "@edu/shared";
import { z } from "zod";
import { loadRuntimeConfig } from "./runtime-config.ts";
import { VIEW_ONLY_TEXT, viewOnlyNoticeFor } from "./view.ts";
/**
 * Кольцевой буфер последних отказов API для заметок о неточностях (раздел 6.4
 * `docs/feedback-notes-design.md`).
 *
 * ⚠️ Импорт направлен из `lib` в `features`, а это против обычного направления
 * в проекте. Так сделано осознанно: буфер обязан стоять там, где отказы
 * ПОЯВЛЯЮТСЯ, то есть здесь, а жить он обязан рядом с тем, кто его читает, то
 * есть в модуле фичи. Альтернатива, «завести здесь ещё одно хранилище и отдать
 * фиче геттер», означала бы второй экземпляр буфера в файле, который про
 * заметки не знает ничего. Модуль без React и без зависимостей, в стартовом
 * чанке он весит десятки строк.
 */
import { recordFail } from "../features/feedback/fails.ts";

/**
 * Клиент API.
 *
 * Два правила, оба следуют из модели сессий (раздел 5.10 CLAUDE.md):
 *
 * 1. `credentials: "include"` во ВСЕХ вызовах. Cookie `__Host-edu_session`
 *    это единственное доказательство гостя, что уроки его, и запрос без неё
 *    выглядит для сервера как запрос постороннего. На проде SPA и API живут
 *    на одном origin, но в деве это разные порты, и без include cookie не
 *    поедет вообще
 * 2. Общий обработчик 401. Сессия отзывается мгновенно (кнопка «выйти
 *    везде», истечение), и любая вкладка обязана заметить это сама, а не
 *    показывать пустые списки с загадочной ошибкой
 */

/**
 * Отказ, которого в словаре нет.
 *
 * Телом ошибки бывает не только наш JSON: своё пишут валидация и лимит частоты
 * самого Fastify, а прокси и вовсе может ответить страницей без тела. Такие
 * отказы разбирать по коду нечем, поэтому у них один общий код, а исходная
 * строка остаётся в `message`, то есть ровно там, куда её и показывают ветки по
 * умолчанию.
 */
export type UnknownErrorCode = "unknown_error";

/** Код отказа так, как его видит экран: словарь плюс «мимо словаря». */
export type ApiErrorCode = ErrorCode | UnknownErrorCode;

export class ApiError extends Error {
  readonly status: number;
  /**
   * Код отказа из общего словаря (`packages/shared/src/errors.ts`).
   *
   * Тип, а не строка, намеренно: по коду мы выбираем текст на экране, и до этого
   * сужения переименование кода на сервере проходило проверку типов с обеих
   * сторон, а человек получал «что-то пошло не так». Теперь `case` мимо словаря
   * не собирается.
   */
  readonly code: ApiErrorCode;
  /**
   * Остальные поля тела ошибки. Нужны там, где кода мало: пакетная операция
   * отказывает целиком и называет конкретный элемент, а «не вышло» на десяти
   * объектах не даёт понять даже, какой из них виноват.
   */
  readonly details: Readonly<Record<string, unknown>>;
  /**
   * Замаскированный путь запроса, либо `null`, если писать нечего.
   *
   * Нужен ровно тем, кто попросил тишину (`quietFail`): отказ в кольцевой буфер
   * они пишут сами и позже, а собрать путь заново им нечем, `res.url` до них не
   * доезжает. У всех остальных поле заполнено тем же значением, которое уже
   * записано в буфер, и читать его незачем.
   */
  readonly failPath: string | null;

  constructor(
    status: number,
    code: string,
    details: Record<string, unknown> = {},
    failPath: string | null = null,
  ) {
    // Отказ просмотра кабинета читается человеком как есть: экраны, которые
    // показывают `message` в ветке по умолчанию, покажут его, а не код
    super(code === "view_only" ? VIEW_ONLY_TEXT : `HTTP ${status}: ${code}`);
    this.status = status;
    // Проверка значением, а не приведением типа: чужая строка обязана стать
    // «мимо словаря», а не притвориться нашим кодом
    this.code = isErrorCode(code) ? code : "unknown_error";
    this.details = details;
    this.failPath = failPath;
  }

  /** Имя элемента, на котором отказал пакет, если сервер его назвал. */
  get itemName(): string | null {
    return typeof this.details.itemName === "string" ? this.details.itemName : null;
  }
}

let onUnauthorized: (() => void) | null = null;

/** Ставит провайдер сессии. Один на приложение: сессия тоже одна. */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

let onViewOnly: (() => void) | null = null;

/**
 * Отказ `view_only`: пишущий запрос под просмотром кабинета админом (раздел 12
 * `docs/admin-panel-design.md`). Ставит полоса просмотра, одна на приложение.
 *
 * Зовётся из `errorOf`, а не из `request()`, по той же причине, что буфер
 * отказов: загрузки и погашение ссылки ходят мимо `request()`.
 */
export function setViewOnlyHandler(fn: (() => void) | null): void {
  onViewOnly = fn;
}

export interface Init {
  method?: string;
  body?: unknown;
  /**
   * Не дёргать общий обработчик на 401. Нужно ровно одному вызову: проверке
   * «а есть ли вообще сессия» при старте, где 401 это нормальный ответ, а не
   * потеря сессии.
   */
  silent401?: boolean;
  /** Отправка при закрытии вкладки: браузер дошлёт запрос после выгрузки. */
  keepalive?: boolean;
  /**
   * Через сколько миллисекунд бросить ожидание ответа.
   *
   * ⚠️ **По умолчанию тайм-аута нет, и это осознанно.** Загрузка материала на
   * 128 кбит/с идёт минутами, и общий потолок означал бы обрыв нормальной
   * работы у ровно того пользователя, ради которого продукт построен. Ставится
   * точечно там, где висящий запрос виден человеку как пустой экран: вход в
   * урок (раздел 2.5 `docs/room-preflight-design.md`).
   *
   * Зачем вообще: сегодня запрос, попавший под отсечку на 16 КБ или под
   * 128 кбит/с (раздел 3 CLAUDE.md), висит бесконечно, и экран показывает
   * «Открываем урок…» столько же. Отличить «медленно» от «не доедет» без
   * тайм-аута нечем.
   *
   * ⚠️ Загрузку медиа это не трогает: у неё свой `AbortController` и своя
   * отмена по кнопке, она ходит мимо `request()`.
   */
  timeoutMs?: number;
  /**
   * Не писать отказ в кольцевой буфер заметок обратной связи.
   *
   * ⚠️ Нужен двум местам. Первое это цикл ретраев за пропуском на доску
   * (`features/board/ticket-retry.ts`). Буфер держит ТРИ последних отказа, а
   * один цикл это до четырёх походов за тикетом, то есть без тишины каждая
   * секундная ямка сети выносила бы из буфера всё содержательное ровно к тому
   * моменту, когда человек откроет окно заметки (раздел 3.1
   * `docs/node-split-design.md`).
   *
   * ⚠️ Тишина это не «отказа не было»: путь приезжает в `ApiError.failPath`, и
   * тот, кто попросил тишину, обязан записать в буфер ровно один отказ за цикл
   * сам. Иначе мы чиним шум ценой слепоты.
   *
   * Второе это опрос кнопки «Задания из PDF» (`getLatestExtraction`): там
   * отказ это не работа человека, а справка, которой нет, и записывать в буфер
   * нечего вовсе; без ручек на сервере (404) иначе писала бы каждая заготовка.
   */
  quietFail?: boolean;
}

/**
 * Ожидание оборвалось по нашему тайм-ауту, а не по отказу сервера.
 *
 * `AbortSignal.timeout` отменяет запрос причиной `DOMException` с именем
 * `TimeoutError`, и это единственный способ отличить «ответа не было» от
 * «ответ был отрицательный». Разница видна человеку: у первого чинится сеть, у
 * второго доступ, и в телеметрии входа они тоже разные исходы (`timeout`
 * против `fail`, см. `packages/shared/src/preflight.ts`).
 */
export function isTimeoutError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "TimeoutError";
}

/**
 * Путь отказа в том виде, в каком его можно положить в заметку, либо `null`,
 * если класть нечего.
 *
 * ⚠️ **Маскировка обязательна, и она здесь единственная защита.** Путь погашения
 * ссылки это `/v1/invites/<код>/redeem`, а код приглашения это секрет, дающий
 * доступ к паре. Заметка живёт в базе 180 суток, то есть один отказ на этом
 * пути без маскировки означал бы живой секрет в базе на полгода. Раньше это
 * спасалось случайностью: `redeemInvite` ходит мимо `request()`, и хук,
 * стоявший бы в `request()`, его бы не увидел. Хук стоит в `errorOf`, значит
 * этот путь сюда доезжает, и словарь `maskApiPath` из `@edu/shared` остаётся
 * последним, кто отделяет секрет от базы.
 *
 * ⚠️ Отказы `/v1/telemetry/*` не пишутся вовсе: телеметрия ICE шлётся с урока
 * постоянно, и на плохой сети она забила бы все три места буфера собственным
 * шумом ровно к тому моменту, когда преподаватель откроет окно заметки.
 *
 * `res.url` бывает пустым (ответ, собранный не сетевым слоем), и тогда `new
 * URL` бросает. Это не повод ронять разбор отказа: без пути просто не пишем.
 */
function failPath(url: string): string | null {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return null;
  }
  if (pathname.startsWith("/v1/telemetry")) return null;
  return maskApiPath(pathname);
}

/** Путь без маскировки, для правила `viewOnlyNoticeFor`. Пустая строка, если адреса нет. */
function pathOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return "";
  }
}

/**
 * ⚠️ Буфер отказов заполняется ЗДЕСЬ, а не в `request()`, и это не вкусовщина.
 * `errorOf` зовут ещё из трёх мест помимо `request`: `redeemInvite`,
 * `uploadMediaPreview` и отправка куска загрузки. Два последних это ровно те
 * отказы, про которые преподаватель пишет чаще всего («файл не грузится»,
 * «превью не появилось»), и они ходят мимо `request()`, потому что тело у них
 * двоичное. Хук в `request()` не увидел бы их никогда, то есть заметка про
 * загрузку приезжала бы без единого следа той самой загрузки.
 */
async function errorOf(res: Response, quiet = false): Promise<ApiError> {
  let code = `http_${res.status}`;
  let details: Record<string, unknown> = {};
  try {
    const body: unknown = await res.json();
    if (typeof body === "object" && body !== null) {
      const maybe = (body as { error?: unknown }).error;
      if (typeof maybe === "string") code = maybe;
      details = body as Record<string, unknown>;
    }
  } catch {
    /* тело не JSON: кода нет, остаётся статус */
  }
  const path = failPath(res.url);
  if (path !== null && !quiet) recordFail(res.status, code, path);
  if (code === "view_only" && viewOnlyNoticeFor(pathOf(res.url))) onViewOnly?.();
  return new ApiError(res.status, code, details, path);
}

/**
 * `signal` для `fetch`, либо ничего.
 *
 * ⚠️ Наличие `AbortSignal.timeout` проверяется, а не предполагается. Метод есть
 * с Safari 16, то есть с iOS 16, а в этом же файле проекта живёт фоллбэк
 * `height: 100vh` «для iOS < 15.4»: аудитория со старыми телефонами у нас
 * заявлена явно. Голый вызов уронил бы там не тайм-аут, а сам запрос урока, то
 * есть страховка от зависания сломала бы вход целиком.
 *
 * Условным разворотом, а не `signal: undefined`: при exactOptionalPropertyTypes
 * явный undefined не то же самое, что отсутствие свойства.
 */
function timeoutSignal(ms: number | undefined): { signal?: AbortSignal } {
  if (ms === undefined) return {};
  if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") return {};
  return { signal: AbortSignal.timeout(ms) };
}

async function request(path: string, init: Init): Promise<Response> {
  const cfg = await loadRuntimeConfig();
  const res = await fetch(`${cfg.apiBaseUrl}${path}`, {
    method: init.method ?? "GET",
    credentials: "include",
    ...(init.body === undefined
      ? {}
      : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(init.body) }),
    ...(init.keepalive === true ? { keepalive: true } : {}),
    // Условным разворотом, а не `signal: init.timeoutMs && ...`: при
    // exactOptionalPropertyTypes явный undefined не то же самое, что отсутствие
    // свойства, а `signal: undefined` некоторые движки разбирают строже
    ...timeoutSignal(init.timeoutMs),
  });

  if (res.status === 401 && init.silent401 !== true) onUnauthorized?.();
  if (!res.ok) throw await errorOf(res, init.quietFail === true);
  return res;
}

/**
 * Ответ сервера, который эта вкладка разобрать не смогла.
 *
 * Причина у такого почти всегда одна: сервер знает поле, которого не знает
 * бандл. Схемы у нас дискриминированные юнионы, и неизвестный вариант роняет
 * разбор ВСЕГО ответа, а не одного поля, поэтому «заготовка не открылась»
 * бывает следствием одной новой подложки на одном слайде.
 *
 * ⚠️ Без этой обёртки на экран уезжает `e.message` от zod: несколько экранов
 * JSON с регулярным выражением для uuid внутри. Человеку это не говорит
 * ничего, а сказать надо ровно одно, и оно почти всегда верное: обновите
 * страницу. Помогает эта правка, разумеется, только тем вкладкам, которые уже
 * с ней приехали, поэтому она не отменяет порядок выкатки (api и collab раньше
 * web), а страхует следующий раз.
 */
export class ResponseFormatError extends Error {
  readonly path: string;

  constructor(path: string, cause: unknown) {
    super(
      "Эта вкладка не понимает ответ сервера: скорее всего, приложение обновилось. Обновите страницу",
      // Настоящая ошибка zod остаётся здесь: на экране она бесполезна, а в
      // консоли это единственное, по чему видно, какое поле не разобралось
      { cause },
    );
    this.path = path;
  }
}

/**
 * ⚠️ `call` и `callVoid` экспортируются наружу для фетчеров, которым нельзя
 * жить в этом файле: четыре ручки второго слоя (`features/exercises/answers-api.ts`),
 * покрытие карты тем (`features/coverage/coverage-api.ts`) и озвучка
 * (`features/tts/tts-api.ts`).
 *
 * У второго слоя причина жёсткая: его ответы несут `ExerciseKey`, который живёт
 * в `@edu/shared/checking`, а этот подпуть не имеет права попасть в стартовый
 * чанк. Конвенция «разбор ответа схемой, импортированной значением, прямо
 * здесь» вела бы туда напрямую: положить схему сюда значит утащить `checking`
 * всем, а не разбирать ответ значит молча сломать конвенцию. У карты причина
 * мягче, только вес: её схема секретов не несёт, но нужна одному ленивому
 * экрану. У озвучки причина та же, что у карты: вместе со схемами ответов сюда
 * приехал бы весь модуль `tts.ts` со словарём голосов и словарём отказов.
 * Поэтому такие фетчеры живут в ленивых чанках, а отсюда наружу уезжают только
 * два хелпера.
 *
 * Второй клиент HTTP заводить нельзя: здесь сидят базовый адрес, cookie, разбор
 * отказов в `ApiError` и `ResponseFormatError`.
 */
export async function call<T>(
  path: string,
  init: Init,
  schema: { parse: (v: unknown) => T },
): Promise<T> {
  const res = await request(path, init);
  const body: unknown = await res.json();
  try {
    return schema.parse(body);
  } catch (e) {
    throw new ResponseFormatError(path, e);
  }
}

/**
 * Ответ вместе с «сейчас» сервера из заголовка `Date` (`null`, если его нет
 * или он не читается). По нему экраны считают остаток жизни подписей
 * (`features/media/hold-token.ts`): часы компьютера могут уйти на минуты.
 * Стенд ходит в API со своего же адреса, поэтому заголовок виден; в деве
 * адреса разные, CORS его прячет, и срок считается по часам вкладки.
 */
export interface Dated<T> {
  value: T;
  serverNow: number | null;
}

async function callDated<T>(path: string, init: Init, schema: { parse: (v: unknown) => T }): Promise<Dated<T>> {
  const res = await request(path, init);
  const date = Date.parse(res.headers.get("Date") ?? "");
  const body: unknown = await res.json();
  try {
    return { value: schema.parse(body), serverNow: Number.isFinite(date) ? date : null };
  } catch (e) {
    throw new ResponseFormatError(path, e);
  }
}

export async function callVoid(path: string, init: Init): Promise<void> {
  await request(path, init);
}

// ------------------------------------------------------------------ сессия

export function getMe(): Promise<MeResponse> {
  return call("/v1/auth/me", { silent401: true }, MeResponse);
}

/**
 * Подтвердить часовой пояс.
 *
 * ⚠️ Именно подтвердить, а не «сообщить догадку браузера». Догадка
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` ошибается на VPN, на
 * чужом ноутбуке и в командировке, а от этого значения зависит время каждого
 * занятия. Поэтому запрос уходит только по нажатию человека.
 *
 * Канонизирует имя сервер: у него и у браузера разные версии ICU, и решать,
 * какая строка ляжет в базу, обязана одна сторона.
 */
export function setTimeZone(timeZone: string): Promise<MeResponse> {
  return call("/v1/me/time-zone", { method: "PUT", body: { timeZone } }, MeResponse);
}

/**
 * Переключатели учёта: посещения и оплаты. Одна ручка на всех учеников.
 *
 * Отвечает целым `MeResponse`, а не одними переключателями: клиент принимает
 * этот ответ ЗА СЕССИЮ, ровно как ответ подтверждения часового пояса. Иначе
 * пришлось бы либо перечитывать `me`, либо собирать сессию из двух источников.
 */
export function setTracking(patch: UpdateTrackingRequest): Promise<MeResponse> {
  return call("/v1/me/tracking", { method: "PUT", body: patch }, MeResponse);
}

/**
 * Предметы, которые ведёт человек. По ним кабинет и комната прячут инструменты
 * чужих предметов. Отвечает целым `MeResponse` по той же причине, что
 * переключатели учёта: клиент принимает ответ ЗА СЕССИЮ.
 */
export function setSubjects(subjects: readonly Subject[]): Promise<MeResponse> {
  return call("/v1/me/subjects", { method: "PUT", body: { subjects } }, MeResponse);
}

/**
 * Предмет урока. У занятия группы сервер пишет его всем урокам комнаты. Ответ
 * 204 и пустой, отсюда `callVoid`.
 */
export function setLessonSubject(lessonId: string, subject: Subject): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}/subject`, { method: "PUT", body: { subject } });
}

/**
 * Своя подпись над курсором на доске.
 *
 * Пустая строка это законное значение и означает «убрать своё, вернуть
 * умолчание»: сервер кладёт NULL. Отвечает целым `MeResponse` по той же
 * причине, что переключатели учёта: клиент принимает ответ ЗА СЕССИЮ.
 */
export function setBoardName(boardName: string): Promise<MeResponse> {
  return call("/v1/me/board-name", { method: "PUT", body: { boardName } }, MeResponse);
}

/**
 * Отметить список «Что нового» прочитанным до даты `seenAt`.
 *
 * ⚠️ `seenAt` это дата САМОЙ СВЕЖЕЙ ПОКАЗАННОЙ записи, а не `Date.now()`:
 * записи живут в бандле, и вкладка с недельным бандлом, отметив «сейчас»,
 * погасила бы записи, которых человек не видел. Разбор в `features/changelog/select.ts`.
 *
 * Отвечает целым `MeResponse` по той же причине, что переключатели учёта:
 * клиент принимает этот ответ ЗА СЕССИЮ.
 */
export function markChangelogSeen(seenAt: number): Promise<MeResponse> {
  return call("/v1/me/changelog-seen", { method: "POST", body: { seenAt } }, MeResponse);
}

/**
 * Сетка расписания на окно.
 *
 * ⚠️ `tz` уходит ВСЕГДА, хотя у подтвердившего пояс человека сервер его
 * игнорирует. Причина в том, что до подтверждения показывать что-то надо, а
 * молчаливого UTC на сервере нет ни в одной ветке: без параметра он ответил бы
 * `400 time_zone_required`, и раздел был бы пустым ровно у того, кто ещё не
 * дошёл до полосы подтверждения.
 */
export function getSchedule(args: { from: string; to: string; tz: string }): Promise<ScheduleResponse> {
  const q = new URLSearchParams({ from: args.from, to: args.to, tz: args.tz });
  return call(`/v1/schedule?${q.toString()}`, {}, ScheduleResponse);
}

// ------------------------------------------------------------- серии занятий

export function listSeries(): Promise<ScheduleSeriesListResponse> {
  return call("/v1/schedule/series", {}, ScheduleSeriesListResponse);
}

export function getSeries(seriesId: string): Promise<ScheduleSeriesView> {
  return call(`/v1/schedule/series/${seriesId}`, {}, ScheduleSeriesView);
}

/**
 * Завести серию вместе со слотами.
 *
 * ⚠️ Слоты едут ОДНИМ запросом с серией, а не «создать серию, потом добавить
 * дни»: серия без слотов не производит ни одного занятия, и оставить её такой
 * после первого же обрыва сети означало бы пустую строку в списке, которую
 * никто не опознает.
 */
export function createSeries(body: CreateSeriesRequest): Promise<ScheduleSeriesView> {
  return call("/v1/schedule/series", { method: "POST", body }, ScheduleSeriesView);
}

export function updateSeries(
  seriesId: string,
  body: UpdateSeriesRequest,
): Promise<ScheduleSeriesView> {
  return call(`/v1/schedule/series/${seriesId}`, { method: "PATCH", body }, ScheduleSeriesView);
}

/**
 * Завершить серию пары с даты: занятий с `from` больше нет.
 *
 * Без `from` сервер сам берёт самую раннюю дату, которая не задевает начавшиеся
 * занятия и проведённые уроки (так зовёт экран серии). Серия без прошлых
 * занятий удаляется целиком, с ними закрывается датой. Если что-то теряется,
 * ответ `409 series_end_confirm` с перечнем, и повтор идёт с `force` вместе с
 * `expectLessonIds` и `expectDeletes` из показанного перечня (разбор в
 * `packages/shared/src/series-end.ts`).
 */
export function endSeries(seriesId: string, body: EndSeriesRequest): Promise<EndSeriesResponse> {
  return call(`/v1/schedule/series/${seriesId}/end`, { method: "POST", body }, EndSeriesResponse);
}

export function addSlot(seriesId: string, body: ScheduleSlotInput): Promise<ScheduleSeriesView> {
  return call(
    `/v1/schedule/series/${seriesId}/slots`,
    { method: "POST", body },
    ScheduleSeriesView,
  );
}

/**
 * Правка слота.
 *
 * ⚠️ Слот правится НА МЕСТЕ, его `id` не меняется никогда: он первая половина
 * ключа занятия. Отсюда и `force`: смена дня недели или времени двигает
 * `starts_on` на дату применения, и подготовленные уроки на датах раньше неё
 * остаются без своего занятия. Сервер отказывает `409 lessons_orphaned` со
 * списком, а `force` это подтверждение человека, который этот список увидел.
 */
export function updateSlot(
  seriesId: string,
  slotId: string,
  body: UpdateSlotRequest,
): Promise<ScheduleSeriesView> {
  return call(
    `/v1/schedule/series/${seriesId}/slots/${slotId}`,
    { method: "PATCH", body },
    ScheduleSeriesView,
  );
}

// ------------------------------------------------------- отмена и перенос

/**
 * Занятие адресуется своим ключом: слот плюс ИСХОДНАЯ локальная дата.
 *
 * ⚠️ Именно исходная, а не та, куда занятие перенесли. Ключ занятия неподвижен,
 * сколько бы раз его ни двигали, поэтому отмена перенесённого занятия это тот
 * же адрес, что и всё остальное с ним.
 */
function occurrencePath(slotId: string, occursOn: string): string {
  return `/v1/schedule/occurrences/${slotId}/${occursOn}`;
}

export function cancelOccurrence(
  slotId: string,
  occursOn: string,
  body: CancelOccurrenceRequest,
): Promise<ScheduleExceptionView> {
  return call(
    `${occurrencePath(slotId, occursOn)}/cancel`,
    { method: "POST", body },
    ScheduleExceptionView,
  );
}

export function moveOccurrence(
  slotId: string,
  occursOn: string,
  body: MoveOccurrenceRequest,
): Promise<ScheduleExceptionView> {
  return call(
    `${occurrencePath(slotId, occursOn)}/move`,
    { method: "POST", body },
    ScheduleExceptionView,
  );
}

/**
 * Индивидуальная правка занятия: своя заготовка, своё название, своя
 * длительность.
 *
 * ⚠️ Тот же адрес занятия, что у отмены и переноса, но правка живёт СВОЕЙ
 * строкой: занятие бывает одновременно перенесённым и с другой заготовкой.
 * Из этого же следует, что «вернуть занятие» правку не снимает.
 *
 * ⚠️ Каждое поле трёхзначно: отсутствие означает «не трогать», `null` означает
 * «вернуть как у серии». Иначе поменять название, не потеряв заготовку, было бы
 * нельзя.
 */
export function patchOccurrence(
  slotId: string,
  occursOn: string,
  body: OccurrenceOverrideRequest,
): Promise<OccurrenceOverrideView> {
  return call(occurrencePath(slotId, occursOn), { method: "PATCH", body }, OccurrenceOverrideView);
}

/** Снять отмену или перенос: занятие возвращается на своё место. */
export function clearOccurrence(slotId: string, occursOn: string): Promise<void> {
  return callVoid(`${occurrencePath(slotId, occursOn)}/exception`, { method: "DELETE" });
}

/**
 * Войти в занятие: создать его урок либо получить уже созданный.
 *
 * ⚠️ Отдельная ручка, а не `POST /v1/lessons`: та берёт преподавателя из
 * сессии, и ученик по ней урок не создаст никогда. Это единственный путь, по
 * которому урок может завести не преподаватель, поэтому у неё свой лимит
 * частоты и своя проверка того, что занятие вообще производится расписанием.
 *
 * Повторное нажатие отдаёт ТОТ ЖЕ урок, а не второй: два урока на одно занятие
 * это две доски.
 */
export function enterOccurrence(slotId: string, occursOn: string): Promise<Lesson> {
  return call(`${occurrencePath(slotId, occursOn)}/lesson`, { method: "POST" }, Lesson);
}

// --------------------------------------------------------- расписание группы

/**
 * Девять ручек пучка (`apps/api/src/routes/schedule/group-*.ts`, проект целиком
 * в `docs/group-lessons-design.md`).
 *
 * ⚠️ Живут здесь, а не в `features/groups/groups-api.ts`, где лежат семь ручек
 * самой группы: звать эти будут экраны расписания, а импорта из чужой фичи в
 * проекте не заведено ни разу.
 *
 * ⚠️ **Адрес пучка это САМА ГРУППА.** Своего идентификатора у расписания группы
 * нет и заводить его не надо: у группы одно действующее расписание, а «разное
 * время в разные дни» это несколько слотов внутри пучка. Поэтому `seriesId` в
 * этих путях не встречается нигде, хотя серий под ними столько же, сколько
 * участников.
 *
 * ⚠️ **Схемы запроса у слота и у занятия ровно те же, что у одиночных**
 * (`UpdateSlotRequest`, `CancelOccurrenceRequest`, `MoveOccurrenceRequest`), то
 * есть групповой режим формы это подстановка адреса, а не второй компонент.
 * Своя схема только у заведения расписания: в ней нет ни `studentUserId`
 * (состав берётся из группы), ни заметки (заметку пишут ПРО ученика, а пучок
 * это не ученик).
 */

/**
 * Действующее расписание группы.
 *
 * ⚠️ Расписания может не быть, и это два обычных состояния экрана, а не сбой:
 * его ещё не заводили либо только что распустили. Сервер отвечает
 * `404 series_not_found`, и разобрать этот отказ обязан тот, кто звал.
 */
export function getGroupSchedule(groupId: string): Promise<GroupScheduleView> {
  return call(`/v1/schedule/groups/${groupId}/series`, {}, GroupScheduleView);
}

/**
 * Завести расписание группы: сервер создаёт по серии на каждого участника.
 *
 * ⚠️ Кто серии не получил, приезжает списком `skipped`, и показать его обязан
 * тот, кто звал: отвязанная пара новых договорённостей не заводит, но из группы
 * отвязка не выкидывает, поэтому такой человек остаётся в составе и молча
 * остаётся без занятий. Молчаливая деградация запрещена 5.5 CLAUDE.md.
 */
export function createGroupSchedule(
  groupId: string,
  body: CreateGroupScheduleRequest,
): Promise<GroupScheduleView> {
  return call(`/v1/schedule/groups/${groupId}/series`, { method: "POST", body }, GroupScheduleView);
}

/**
 * Правка пучка целиком: зона, конец, длительность, название, заготовка, внешний
 * созвон.
 *
 * ⚠️ Начала пучка в теле нет намеренно, и дописать его сюда нельзя: у
 * догнавшего в середине четверти начало своё, и общий сдвиг либо стёр бы его
 * дату, либо задним числом добавил бы человека в занятия, которых у него не
 * было. Двигаются слоты (`updateGroupSlot`), а не начало.
 */
export function updateGroupSchedule(
  groupId: string,
  body: UpdateGroupScheduleRequest,
): Promise<GroupScheduleView> {
  return call(
    `/v1/schedule/groups/${groupId}/series`,
    { method: "PATCH", body },
    GroupScheduleView,
  );
}

/**
 * Распустить расписание группы: занятий больше не будет.
 *
 * ⚠️ Ответ 204 и пустой, поэтому `callVoid`: разбирать нечего, а `call` упал бы
 * на `res.json()`. Обновлять состояние экрана из ответа тоже нечем, и это не
 * упущение контракта: действующего пучка у группы после роспуска больше нет, то
 * есть `getGroupSchedule` теперь ответит 404, а экран переходит в состояние
 * «расписания нет».
 *
 * ⚠️ Это `ends_on`, а не удаление: прошлые занятия остаются занятиями группы, и
 * новое расписание заводится обычным `createGroupSchedule`.
 */
export function disbandGroupSchedule(groupId: string): Promise<void> {
  return callVoid(`/v1/schedule/groups/${groupId}/series/disband`, { method: "POST" });
}

/** Добавить группе день недели: слот заводится каждому участнику разом. */
export function addGroupSlot(groupId: string, body: ScheduleSlotInput): Promise<GroupScheduleView> {
  return call(`/v1/schedule/groups/${groupId}/slots`, { method: "POST", body }, GroupScheduleView);
}

/**
 * Правка слота пучка на месте, у всех участников сразу.
 *
 * ⚠️ `slotId` это ПРЕДСТАВИТЕЛЬ пучка, а не единственный слот: настоящих
 * столько же, сколько участников, наружу едет один. Берётся он из `slots`
 * расписания либо из строки сетки, руками не собирается.
 *
 * ⚠️ `force` в теле один на весь пучок, и список осиротевших уроков в отказе
 * `409 lessons_orphaned` сводный по всем участникам: занятие у них общее,
 * подтверждать по одному ученику нечего.
 */
export function updateGroupSlot(
  groupId: string,
  slotId: string,
  body: UpdateSlotRequest,
): Promise<GroupScheduleView> {
  return call(
    `/v1/schedule/groups/${groupId}/slots/${slotId}`,
    { method: "PATCH", body },
    GroupScheduleView,
  );
}

/**
 * Занятие группы адресуется тем же ключом, что индивидуальное: слот плюс
 * ИСХОДНАЯ локальная дата. Разница одна: слот здесь представитель пучка, и в
 * слоты остальных участников его разрешает сервер.
 */
function groupOccurrencePath(groupId: string, slotId: string, occursOn: string): string {
  return `/v1/schedule/groups/${groupId}/occurrences/${slotId}/${occursOn}`;
}

/**
 * Отменить занятие группы: у всех участников сразу.
 *
 * ⚠️ `affected` в ответе это число задетых пар, и оно же подтверждение того, что
 * операция была атомарной: половины здесь не бывает, помеха у одной пары это
 * отказ на всю отмену.
 */
export function cancelGroupOccurrence(
  groupId: string,
  slotId: string,
  occursOn: string,
  body: CancelOccurrenceRequest,
): Promise<GroupOccurrenceView> {
  return call(
    `${groupOccurrencePath(groupId, slotId, occursOn)}/cancel`,
    { method: "POST", body },
    GroupOccurrenceView,
  );
}

/**
 * Перенести занятие группы.
 *
 * ⚠️ `occursOn` в адресе остаётся ИСХОДНОЙ датой навсегда, переезжает только
 * момент. Столкновение, найденное хоть у одной пары, отменяет перенос у всех.
 */
export function moveGroupOccurrence(
  groupId: string,
  slotId: string,
  occursOn: string,
  body: MoveOccurrenceRequest,
): Promise<GroupOccurrenceView> {
  return call(
    `${groupOccurrencePath(groupId, slotId, occursOn)}/move`,
    { method: "POST", body },
    GroupOccurrenceView,
  );
}

/**
 * Снять отмену или перенос: занятие группы возвращается на своё место.
 *
 * ⚠️ Ответ 204, как и у одиночного `clearOccurrence`, поэтому расписание после
 * возврата экран перечитывает сам.
 */
export function clearGroupOccurrence(
  groupId: string,
  slotId: string,
  occursOn: string,
): Promise<void> {
  return callVoid(`${groupOccurrencePath(groupId, slotId, occursOn)}/exception`, {
    method: "DELETE",
  });
}

// -------------------------------------------------------- разовое занятие

/**
 * Завести разовое занятие: «давайте дополнительно в субботу».
 *
 * ⚠️ Дата, время и зона едут ТЕКСТОМ, а момент считает сервер. Своей
 * арифметикой браузер здесь не занимается намеренно: ICU в браузере это другая
 * база часовых поясов, и слепок, посчитанный тут, разошёлся бы с занятиями
 * серий на зоне, чьи правила поменялись между релизами.
 */
export function createSingleLesson(body: CreateSingleLessonRequest): Promise<Lesson> {
  return call("/v1/schedule/lessons", { method: "POST", body }, Lesson);
}

/**
 * Перенести разовое занятие.
 *
 * ⚠️ Не путать с `moveOccurrence`: у занятия серии перенос это строка
 * исключения, знающая и «откуда», и «куда», а у разового переезжает сам урок.
 * Урок со слотом эта ручка отвергает `409 occurrence_managed`.
 */
export function moveSingleLesson(
  lessonId: string,
  body: UpdateSingleLessonRequest,
): Promise<Lesson> {
  return call(`/v1/schedule/lessons/${lessonId}`, { method: "PATCH", body }, Lesson);
}

/**
 * Своя ссылка занятия на внешний созвон (Zoom, Телемост, Meet).
 *
 * ⚠️ Адресуется УРОК, а не занятие, и это не описка. Колонки в
 * `schedule_occurrence_override` нет намеренно (разбор в шапке миграции
 * `1788393600000_external_call_url`), поэтому переопределить ссылку можно
 * только у уже подготовленного занятия. У ещё не подготовленного действует
 * ссылка серии, и это единственное, что там есть.
 *
 * `null` означает «убрать свою»: у занятия серии оно возвращает наследование,
 * у разового занятия серии нет вовсе, и там это буквально «ссылки нет».
 */
export function setLessonExternalCallUrl(lessonId: string, url: string | null): Promise<Lesson> {
  return call(
    `/v1/schedule/lessons/${lessonId}/external-call-url`,
    { method: "PUT", body: { url } },
    Lesson,
  );
}

// ------------------------------------------------ самозапись на открытое время

/**
 * Открыть время для записи: одно окно или пачку «каждую неделю до»
 * (`docs/booking-design.md`, 1.6). Пропущенное приезжает в `skipped` с
 * причиной, а не отказом: пачка частично открывается, и назвать дырки
 * преподавателю обязан сам диалог.
 */
export function createOpenings(body: CreateOpeningsRequest): Promise<CreateOpeningsResponse> {
  return call("/v1/booking/openings", { method: "POST", body }, CreateOpeningsResponse);
}

/** Закрыть своё открытое окно. У занятого `409 opening_booked`. */
export function closeOpening(openingId: string): Promise<void> {
  return callVoid(`/v1/booking/openings/${openingId}`, { method: "DELETE" });
}

/**
 * Записаться на окно. ⚠️ Любая причина отказа это один `409 opening_taken`:
 * различать их ученику нельзя (1.5 проекта), поэтому и текст на экране один.
 */
export function bookOpening(openingId: string): Promise<Lesson> {
  return call(`/v1/booking/openings/${openingId}/book`, { method: "POST" }, Lesson);
}

/** Отменить свою запись. Урок уходит в корзину преподавателя. */
export function cancelBooking(lessonId: string): Promise<void> {
  return callVoid(`/v1/booking/lessons/${lessonId}/cancel`, { method: "POST" });
}

/**
 * Общая часть всех ответов входа: кто вошёл и до когда. Токена здесь нет
 * намеренно, браузеру он не нужен: сессия едет в cookie `__Host-`, которую JS
 * не читает. Токен в теле существует ради curl на edu-api, куда эта cookie не
 * приезжает физически.
 */
export type SessionLike = Omit<SessionResponse, "token">;

/**
 * Вход по логину и паролю.
 *
 * `silent401: true`: на экране входа 401 это нормальный ответ («не сошлось»),
 * а не потеря сессии. Без этого общий обработчик дёргал бы «вас разлогинили»
 * на каждую опечатку в пароле.
 *
 * Сервер отвечает одинаково и на неизвестный логин, и на неверный пароль,
 * поэтому различать их в интерфейсе нечем и не нужно.
 */
export function signInWithPassword(login: string, password: string): Promise<SessionResponse> {
  return call(
    "/v1/auth/password",
    { method: "POST", body: { login, password }, silent401: true },
    SessionResponse,
  );
}

/**
 * Гостевой вход. Существует, только если сервер его разрешил
 * (`ALLOW_GUEST_SIGNUP`); иначе маршрута нет вовсе и ответ будет 404.
 * Кнопку рисуем по флагу из config.json, чтобы до этого не доходило.
 */
export function signInAsGuest(displayName: string): Promise<SessionResponse> {
  return call("/v1/auth/guest", { method: "POST", body: { displayName } }, SessionResponse);
}

export function signOut(): Promise<void> {
  return callVoid("/v1/auth/logout", { method: "POST" });
}

/**
 * Обмен одноразового кода из панели на просмотр кабинета преподавателя, только
 * на чтение (раздел 12.4 `docs/admin-panel-design.md`).
 *
 * Ответ это `MeResponse` с заполненным `viewer` и БЕЗ токена: такая сессия
 * живёт только в cookie. Отказ на любую причину один, 404 `view_code_not_found`.
 */
export function signInWithViewCode(code: string): Promise<MeResponse> {
  return call("/v1/auth/view", { method: "POST", body: { code }, silent401: true }, MeResponse);
}

/**
 * Смена своего пароля. Отдаёт число отозванных ЧУЖИХ сессий: текущая остаётся
 * живой, поэтому после успеха ни перевходить, ни трогать состояние сессии на
 * клиенте не нужно.
 *
 * `silent401: true` по той же причине, что на входе: 401 здесь означает «текущий
 * пароль не сошёлся», а не потерю сессии, и общий обработчик иначе выкидывал бы
 * человека на экран входа за опечатку.
 */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ revoked: number }> {
  return call(
    "/v1/auth/password/change",
    { method: "POST", body: { currentPassword, newPassword }, silent401: true },
    z.object({ revoked: z.number().int() }),
  );
}

/**
 * Адрес начала входа через Google. Именно адрес, а НЕ вызов `request()`.
 *
 * Переход обязан быть полной навигацией верхнего уровня: сервер отвечает 302
 * на `accounts.google.com`, и `fetch` этот редирект попытается пройти сам,
 * упрётся в CORS чужого домена и получит непрозрачную ошибку. Плюс экран
 * согласия человек должен увидеть в адресной строке, иначе он не понимает,
 * куда вводит пароль.
 *
 * Не «унифицировать» через общий клиент: единственное, что тут общего, это
 * базовый адрес.
 */
export async function googleStartUrl(): Promise<string> {
  const cfg = await loadRuntimeConfig();
  return `${cfg.apiBaseUrl}/v1/auth/google/start`;
}

// ------------------------------------------------------------------- уроки

/**
 * Половина раздела «Уроки»: `active` целиком под потолком (с признаком
 * `truncated`), `ended` страницами по курсору. Правила в шапке
 * `apps/api/src/repo/lesson-list.ts`.
 */
export function listLessons(query: {
  part: LessonListPart;
  cursor?: string | null;
  limit?: number;
}): Promise<LessonListResponse> {
  const params = new URLSearchParams({ part: query.part });
  if (query.cursor != null) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  return call(`/v1/lessons?${params.toString()}`, {}, LessonListResponse);
}

/**
 * Урок по идентификатору.
 *
 * `timeoutMs` нужен ровно одному вызывающему, входу в урок: там висящий запрос
 * это пустой экран без срока и без выхода (2.5 `docs/room-preflight-design.md`).
 * Остальным он ни к чему, поэтому необязателен.
 */
export function getLesson(lessonId: string, opts?: { timeoutMs?: number }): Promise<Lesson> {
  return call(
    `/v1/lessons/${lessonId}`,
    opts?.timeoutMs === undefined ? {} : { timeoutMs: opts.timeoutMs },
    Lesson,
  );
}

/**
 * Проведённые занятия: раздел «Архив».
 *
 * ⚠️ Оба фильтра уезжают в запрос, а не применяются к готовому списку. Отбор
 * делает база по `lesson_teacher_recent_idx`: у преподавателя с историей уроков
 * тысячи, и тянуть их все ради выпадающего списка на канале 128 кбит/с это
 * минуты ожидания (раздел 3.1 CLAUDE.md).
 *
 * Пустая строка значит «фильтр снят» и в запрос не попадает вовсе: `?planId=`
 * сервер разобрал бы как невалидный uuid и ответил 400.
 */
export function listArchive(filter: {
  studentUserId?: string;
  planId?: string;
}): Promise<ArchiveListResponse> {
  const qs = new URLSearchParams();
  if (filter.studentUserId) qs.set("studentUserId", filter.studentUserId);
  if (filter.planId) qs.set("planId", filter.planId);
  const tail = qs.toString();
  return call(`/v1/lessons/archive${tail === "" ? "" : `?${tail}`}`, {}, ArchiveListResponse);
}

export function endLesson(lessonId: string): Promise<Lesson> {
  return call(`/v1/lessons/${lessonId}/end`, { method: "POST" }, Lesson);
}

export function trashLesson(lessonId: string): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}`, { method: "DELETE" });
}

/**
 * Токен LiveKit. Роль и имя сервер берёт из сессии и из app_user, поэтому
 * тело пустое: до M6 клиент присылал роль сам, и это была дыра, а не удобство.
 *
 * 503 `livekit_unavailable` это не поломка, а явная деградация: урок
 * продолжается на доске (четвёртый уровень лестницы, раздел 5.5).
 */
export function requestToken(
  lessonId: string,
  opts?: { timeoutMs?: number },
): Promise<TokenResponse> {
  return call(
    `/v1/lessons/${lessonId}/token`,
    {
      method: "POST",
      body: {},
      ...(opts?.timeoutMs === undefined ? {} : { timeoutMs: opts.timeoutMs }),
    },
    TokenResponse,
  );
}

/**
 * Состав комнаты: кто на этом занятии есть. Только преподавателю, право
 * проверяет сервер.
 *
 * ⚠️ **Это «кто должен быть», а не «кто пришёл».** Пришедших знает присутствие
 * документа, и знает точнее: сервер про открытые вкладки не знает вовсе. Строка
 * «не в комнате» это разность двух источников, и складывает их экран, а не
 * ручка.
 *
 * ⚠️ **Порядок строк задаёт сервер и держит устойчивым между запросами.**
 * Пересортировывать ответ на клиенте не надо: своя сортировка вернула бы ровно
 * ту прыгающую строку, от которой серверная и защищает.
 *
 * Коды отказа. 404 `lesson_not_found` это и «такого урока нет», и «он не ваш»:
 * различать их снаружи нельзя, это подсказка тому, кто перебирает
 * идентификаторы. 409 `entry_closed` означает «окно входа закрылось», то есть
 * занятие кончилось у всех сразу, и приходит он тем же образом, что на пропуск
 * в комнату. Медиасервера ручка не трогает вовсе, поэтому `livekit_unavailable`
 * от неё не приходит никогда.
 */
export function getRoomParticipants(lessonId: string): Promise<RoomRosterResponse> {
  return call(`/v1/lessons/${lessonId}/participants`, {}, RoomRosterResponse);
}

/**
 * Микрофон участника комнаты: выключить и вернуть. Только преподавателю, право
 * проверяет сервер.
 *
 * `on` это «микрофон разрешён», а не «микрофон включён»: возврат права чужой
 * микрофон не включает, включает его ученик сам.
 *
 * 404 `participant_not_found` означает «этого ученика в комнате сейчас нет», а
 * 503 `livekit_unavailable` означает «медиасервер не отвечает». Путать их
 * нельзя: под первым текстом у преподавателя будет поиск несуществующей ошибки.
 */
export function setParticipantMic(
  lessonId: string,
  userId: string,
  on: boolean,
): Promise<MicResponse> {
  return call(
    `/v1/lessons/${lessonId}/participants/${userId}/mic`,
    { method: "POST", body: { on } },
    MicResponse,
  );
}

/**
 * Кто отметил «не понимаю».
 *
 * ⚠️ **Ответ у сторон разный, и это свойство ручки, а не разводки клиента.**
 * Преподавателю приезжает вся комната, ученику не больше одной записи, его
 * собственной, и режет это предикат на сервере. Поэтому ученик про себя
 * спрашивает длину списка, а не ищет в нём свой идентификатор: своего
 * идентификатора у него на экране нет вовсе.
 *
 * ⚠️ **Опрашивает этой ручкой только преподаватель.** Ученик читает её РОВНО
 * ОДИН раз, при входе, ради отметки, поставленной до перезагрузки страницы:
 * дальше он своё состояние знает сам, потому что сам его и ставит.
 */
export function getRoomSignals(lessonId: string): Promise<LessonSignalsResponse> {
  return call(`/v1/lessons/${lessonId}/signals`, {}, LessonSignalsResponse);
}

/**
 * Ученик поставил или снял свою отметку «не понимаю».
 *
 * Одной функцией на оба действия, потому что на экране это одна кнопка с двумя
 * положениями: разведя их, мы получили бы два места, где однажды разойдётся
 * адрес. Ответ 204 и пустой, отсюда `callVoid`.
 */
export function setMyRoomSignal(lessonId: string, on: boolean): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}/signal`, { method: on ? "POST" : "DELETE" });
}

/**
 * Преподаватель погасил отметку ученика.
 *
 * Адресуется уроком КОМНАТЫ плюс учеником, ровно как распоряжение микрофоном:
 * адреса чужих уроков клиенту не отдаются вовсе.
 *
 * ⚠️ Промах по человеку это тоже 204: гасить уже погашенное преподаватель будет
 * постоянно, потому что список обновляется опросом и отстаёт на несколько
 * секунд. Отказа на это ждать не надо.
 */
export function clearRoomSignal(lessonId: string, userId: string): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}/participants/${userId}/signal`, { method: "DELETE" });
}

/**
 * Тикет доски. Живёт 120 секунд и не зависит от медиасервера: именно ради
 * этого он и отделён от токена LiveKit.
 *
 * ⚠️ **Пропуск выписывается на ДОКУМЕНТ, а документов у занятия группы N+1:**
 * общая доска и по документу ответов на каждого ученика (`answersDocIdOf`).
 * Один пропуск на два имени невыразим, потому что collab сверяет поле `lid` с
 * именем документа, поэтому `doc` необязателен только для бандла, выкаченного до
 * разделения: его отсутствие означает доску занятия.
 *
 * Кому что можно, решает СЕРВЕР: ученику свой документ ответов и общая доска,
 * преподавателю любой документ его комнаты. Чужой это 404, и притом
 * неотличимый от «такого урока нет».
 *
 * ⚠️ Зовут эту ручку из цикла ретраев (`features/board/ticket-retry.ts`), и
 * ходит он с `quietFail`: один цикл это до четырёх походов, а буфер отказов
 * держит три записи. Запись в буфер цикл делает сам, ровно одну за цикл.
 */
export function requestBoardTicket(
  lessonId: string,
  doc?: string,
  init: { quietFail?: boolean } = {},
): Promise<BoardTicketResponse> {
  return call(
    `/v1/lessons/${lessonId}/board-ticket`,
    { method: "POST", body: { doc }, ...init },
    BoardTicketResponse,
  );
}

export function getBoardSnapshot(lessonId: string): Promise<BoardSnapshotResponse> {
  return call(`/v1/lessons/${lessonId}/board`, {}, BoardSnapshotResponse);
}

/**
 * Собрать задание по теме прямо на живом уроке. Только преподаватель этого
 * урока, право проверяет сервер.
 *
 * ⚠️ **Схема ответа тут законна, а `@edu/shared/checking` в этом файле не был бы
 * законен, и разница не в раскладке.** Ручка отдаёт ПОКАЗЫВАЕМУЮ половину
 * задания (`ExerciseElement`), то есть то же самое, что и так лежит в общем
 * документе урока и что видит ученик. Эталон она не отдаёт никому, включая
 * преподавателя: он берёт его отдельной ручкой, и та живёт в ленивом чанке
 * (`features/exercises/answers-api.ts`) ровно потому, что несёт `ExerciseKey`.
 *
 * ⚠️ **`elementId` и `taken` придумывает вызывающий, и оба обязаны прийти
 * оттуда.** Первый это ключ идемпотентности: повтор потерянного запроса обязан
 * идти с тем же, иначе одно нажатие поставит на доску два задания. Второй это
 * число заданий на слайде, и знает его только тот, кто смотрит в документ.
 * Рамку по нему считает сервер: до генерации вид задания неизвестен, а от него
 * зависит размер коробки (разбор в `AddLessonExerciseRequest`).
 */
export function addLessonExercise(
  lessonId: string,
  body: AddLessonExerciseRequest,
): Promise<AddLessonExerciseResponse> {
  return call(
    `/v1/lessons/${lessonId}/exercises`,
    { method: "POST", body },
    AddLessonExerciseResponse,
  );
}

/**
 * Собрать тест на весь пройденный курс и получить его разложенным по слайдам.
 * Только преподаватель этого урока, право проверяет сервер.
 *
 * ⚠️ **Один запрос, а не тринадцать вызовов `addLessonExercise`.** Причин три, и
 * каждой хватило бы: у той ручки стоит лимит частоты, и стоит он не случайно;
 * тринадцать вызовов не атомарны, а половина теста на доске хуже, чем его
 * отсутствие; и замок комнаты брался бы тринадцать раз подряд. Разбор в шапке
 * контракта (`packages/shared/src/course-test.ts`).
 *
 * ⚠️ **`testId` придумывает вызывающий, и это ключ идемпотентности.** Повтор
 * потерянного запроса обязан идти с тем же: сборка зовёт генераторы и пишет
 * эталоны, то есть повтор под новым ключом положил бы на доску второй тест. Та
 * же причина и тот же приём, что у `elementId` в `addLessonExercise`.
 *
 * ⚠️ Схема ответа здесь законна ровно по той же причине, что и у соседа: ручка
 * отдаёт ПОКАЗЫВАЕМУЮ половину заданий (`ExerciseElement`), то есть то же
 * самое, что ляжет в общий документ урока и что увидит ученик. Эталонов она не
 * отдаёт никому, включая преподавателя.
 */
export function buildCourseTest(
  lessonId: string,
  body: CourseTestRequest,
): Promise<CourseTestResponse> {
  return call(`/v1/lessons/${lessonId}/course-test`, { method: "POST", body }, CourseTestResponse);
}

/**
 * Собрать пачку заданий по строкам «тема, уровень, количество» и получить её
 * разложенной по слайдам. Только преподаватель этого урока, право проверяет
 * сервер. Проект в `docs/room-exercise-batch-design.md`.
 *
 * ⚠️ **Один запрос на всю пачку, а не N вызовов `addLessonExercise`**, по тем
 * же трём причинам, что у `buildCourseTest` выше: лимит частоты, атомарность и
 * замок комнаты.
 *
 * ⚠️ **`batchId` придумывает вызывающий, и это ключ идемпотентности.** Сервер
 * выводит из него идентификаторы заданий, поэтому повтор потерянного запроса
 * возвращает ту же пачку, а не кладёт вторую.
 *
 * Эталонов ручка не отдаёт никому, включая преподавателя: в ответе только
 * показываемая половина (`ExerciseElement`), как у соседей.
 */
export function buildExerciseBatch(
  lessonId: string,
  body: LessonBatchRequest,
): Promise<LessonBatchResponse> {
  return call(`/v1/lessons/${lessonId}/exercise-batch`, { method: "POST", body }, LessonBatchResponse);
}

/** Состояние урока относительно заготовки. Только для преподавателя. */
export function getLessonPlanLink(lessonId: string): Promise<LessonPlanLink> {
  return call(`/v1/lessons/${lessonId}/plan`, {}, LessonPlanLink);
}

/**
 * Обновить урок материалом заготовки.
 *
 * `force` это подтверждение человека, увидевшего список потерь: оно снимает
 * сверку с живым документом. Занятость комнаты и начатый урок им не снимаются,
 * и отказы `room_busy` и `lesson_started` приходят при любом подтверждении.
 */
export function syncLessonPlan(lessonId: string, force: boolean): Promise<LessonPlanLink> {
  return call(
    `/v1/lessons/${lessonId}/plan/sync`,
    { method: "POST", body: { force } },
    LessonPlanLink,
  );
}

// ----------------------------------------------------------------- ученики

export function listStudents(): Promise<StudentListResponse> {
  return call("/v1/students", {}, StudentListResponse);
}

/**
 * Карточка одного ученика: пара, судьба ссылки, его занятия и доля верных
 * ответов. Одним запросом, потому что экран открывается переходом из списка, а
 * каждый лишний круг по сети на канале 128 кбит/с человек видит как подвисание.
 *
 * Чужой и несуществующий ученик одинаково 404: различать их снаружи значит
 * подсказывать тому, кто перебирает идентификаторы.
 */
export function getStudent(studentUserId: string): Promise<StudentDetail> {
  return call(`/v1/students/${studentUserId}`, {}, StudentDetail);
}

/**
 * Страница занятий пары (единица списка это занятие, а не урок). С курсором это
 * догрузка (курсор первой берётся из карточки, `occurrences.nextCursor`); без
 * курсора и с `limit` это перечитывание первых N строк, чтобы действие на
 * экране не сбрасывало глубину списка. `filter` едет и в том, и в другом:
 * первой страницы под фильтром в карточке нет.
 */
export function getStudentOccurrences(
  studentUserId: string,
  query: { cursor?: string | null; limit?: number; filter?: PairOccurrenceFilter | null },
): Promise<PairOccurrencesResponse> {
  const params = new URLSearchParams();
  if (query.cursor != null) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.filter != null) params.set("filter", query.filter);
  return call(
    `/v1/students/${studentUserId}/occurrences?${params.toString()}`,
    {},
    PairOccurrencesResponse,
  );
}

/**
 * Открыть или закрыть ученику архив его уроков.
 *
 * Действует немедленно и на уже вошедшего: право на архив проверяется на
 * каждый запрос (`requireLessonAccess`), а не выдаётся вместе с сессией.
 */
export function setStudentArchiveVisible(
  studentUserId: string,
  archiveVisible: boolean,
): Promise<void> {
  return callVoid(`/v1/students/${studentUserId}`, {
    method: "PATCH",
    body: { archiveVisible },
  });
}

/**
 * Анкета: режим оплаты и приватная заметка.
 *
 * Своя ручка, а не поля в `setStudentArchiveVisible`: та правит ПРАВО ученика
 * видеть архив, а это записная книжка преподавателя. Смешав их, мы получили бы
 * один обработчик на право и на заметку.
 */
export function updateStudentProfile(
  studentUserId: string,
  patch: UpdateStudentProfileRequest,
): Promise<void> {
  return callVoid(`/v1/students/${studentUserId}/profile`, { method: "PATCH", body: patch });
}

export function addStudentContact(
  studentUserId: string,
  input: ContactInput,
): Promise<StudentContactResponse> {
  return call(
    `/v1/students/${studentUserId}/contacts`,
    { method: "POST", body: input },
    StudentContactResponse,
  );
}

export function updateStudentContact(
  studentUserId: string,
  contactId: string,
  input: ContactInput,
): Promise<StudentContactResponse> {
  return call(
    `/v1/students/${studentUserId}/contacts/${contactId}`,
    { method: "PATCH", body: input },
    StudentContactResponse,
  );
}

export function deleteStudentContact(studentUserId: string, contactId: string): Promise<void> {
  return callVoid(`/v1/students/${studentUserId}/contacts/${contactId}`, { method: "DELETE" });
}

/**
 * Отметить занятие: пришёл ли ученик и заплатил ли за него.
 *
 * `PUT` и состояние в теле, а не два глагола: повтор того же запроса обязан
 * быть безвредным. На канале, где ответ теряется чаще запроса (раздел 3.1
 * CLAUDE.md), клиент повторит, и второй `POST` «оплатить» задвоил бы отметку.
 *
 * ⚠️ Поля независимы, и не присланное означает «не трогай»: посещение и оплата
 * это две кнопки в одной строке, и нажатие на одну не имеет права стирать
 * вторую.
 */
export function setStudentMark(
  studentUserId: string,
  key: OccurrenceKey,
  patch: { paid?: boolean; attendance?: Attendance | null },
): Promise<void> {
  return callVoid(`/v1/students/${studentUserId}/marks`, {
    method: "PUT",
    body: { key, ...patch },
  });
}

/**
 * Подвести черту: отметить оплаченными все занятия до даты.
 *
 * `POST`, а не `PUT`: повтор здесь не безвреден по смыслу, и притворяться
 * идемпотентным значило бы соврать. Отвечает числом закрытых занятий, и ноль
 * законен: экран обязан сказать о нём словами, а не промолчать.
 */
export function settleStudentPayments(
  studentUserId: string,
  before: string,
): Promise<SettlePaymentsResponse> {
  return call(
    `/v1/students/${studentUserId}/marks/settle`,
    { method: "POST", body: { before } },
    SettlePaymentsResponse,
  );
}

/**
 * Отметить месяц оплаченным или снять отметку. Помесячная пара.
 *
 * `PUT` и состояние в теле, как у поурочной отметки: повтор обязан быть
 * безвредным, потому что на канале, где ответ теряется чаще запроса, клиент
 * повторит.
 *
 * ⚠️ Подтверждения у этого действия нет: нажатие на чип ставит, повторное
 * снимает. Решение владельца 21.08.2026, разбор в шапке `StudentMarks.tsx`.
 */
export function setStudentMonth(
  studentUserId: string,
  period: string,
  paid: boolean,
): Promise<void> {
  return callVoid(`/v1/students/${studentUserId}/months`, {
    method: "PUT",
    body: { period, paid },
  });
}

export function createStudent(displayName: string): Promise<StudentWithInvite> {
  return call("/v1/students", { method: "POST", body: { displayName } }, StudentWithInvite);
}

/**
 * Схема ответа перевыпуска описана здесь, а не в packages/shared: на сервере
 * это два поля, собранные прямо в маршруте, и общего типа у них нет. Как
 * только он появится, эта схема уедет туда.
 */
const ReissueResponse = z.object({
  inviteCode: z.string(),
  inviteUrl: z.string(),
});
export type ReissueResponse = z.infer<typeof ReissueResponse>;

export function reissueInvite(studentUserId: string): Promise<ReissueResponse> {
  return call(
    `/v1/students/${studentUserId}/reissue-invite`,
    { method: "POST" },
    ReissueResponse,
  );
}

export function unlinkStudent(studentUserId: string, hideArchive: boolean): Promise<void> {
  return callVoid(`/v1/students/${studentUserId}`, {
    method: "DELETE",
    body: { hideArchive },
  });
}

/**
 * Погашение ссылки. Отдаёт пришедшему уже заведённого преподавателем ученика,
 * а не создаёт нового: ссылка это ключ к конкретному человеку.
 *
 * 409 `already_signed_in` означает «вы уже вошли кем-то другим». Молча
 * подменять сессию нельзя: ссылку могли открыть из вкладки преподавателя.
 *
 * Схема тела 409 переехала в `@edu/shared` (`account-adopt.ts`, 8.1
 * `docs/account-adopt-design.md`): к нему добавилось предложение переноса, и две
 * копии формы однажды разошлись бы. Отсюда она реэкспортируется для
 * `room/lesson-link-gate.tsx`, который берёт её как `api.AlreadySignedIn`.
 */
export { AlreadySignedIn };

export type RedeemResult =
  | { kind: "signed"; session: RedeemInviteResponse }
  | { kind: "conflict"; info: AlreadySignedIn };

export async function redeemInvite(code: string): Promise<RedeemResult> {
  const cfg = await loadRuntimeConfig();
  const res = await fetch(`${cfg.apiBaseUrl}/v1/invites/${code}/redeem`, {
    method: "POST",
    credentials: "include",
  });

  if (res.status === 409) {
    return { kind: "conflict", info: AlreadySignedIn.parse(await res.json()) };
  }
  if (!res.ok) throw await errorOf(res);
  return { kind: "signed", session: RedeemInviteResponse.parse(await res.json()) };
}

/**
 * «Да, это один ученик» на экране вопроса (`POST /v1/invites/:code/adopt`, 8.2
 * `docs/account-adopt-design.md`).
 *
 * Имя не `adopt`: так в вебе уже зовётся `useSession().adopt`, принять сессию.
 *
 * `currentUserId` это вошедший, которого видел экран: сменился за это время
 * (вход в соседней вкладке), и сервер ответит `409 adopt_stale`, а не перенесёт
 * не того. Отказы приходят `ApiError` с кодом `adopt_*`; у `adopt_in_lesson` в
 * `details` признак `stubIsCurrent`. Токена в ответе нет: он не менялся.
 *
 * ⚠️ Путь с кодом уезжает в буфер отказов замаскированным: `maskApiPath` знает
 * `invites` и `adopt`, а код превращает в `:id`.
 */
export function confirmSameStudent(code: string, currentUserId: string): Promise<AdoptResponse> {
  const body: AdoptRequest = { currentUserId, confirm: true };
  return call(`/v1/invites/${code}/adopt`, { method: "POST", body }, AdoptResponse);
}

/**
 * Ссылка ученика на одно занятие 1:1 (`/lesson/<id>?k=`). Только преподаватель
 * урока; повторный запрос отдаёт ту же ссылку.
 */
export function requestStudentLessonLink(lessonId: string): Promise<StudentLessonLinkResponse> {
  return call(
    `/v1/lessons/${lessonId}/student-link`,
    { method: "POST" },
    StudentLessonLinkResponse,
  );
}

/**
 * Погашение ключа занятия. Ответ ровно тот же, что у приглашения: сессия
 * ученика урока либо 409 для уже вошедшего кем-то другим.
 *
 * ⚠️ Ключ едет ТЕЛОМ, а не в пути: путь уезжает в буфер отказов (замаскованным,
 * но всё же), а тело туда не попадает вовсе.
 */
export async function redeemLessonLink(lessonId: string, key: string): Promise<RedeemResult> {
  const cfg = await loadRuntimeConfig();
  const res = await fetch(`${cfg.apiBaseUrl}/v1/lessons/${lessonId}/student-link/redeem`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ key }),
  });

  if (res.status === 409) {
    return { kind: "conflict", info: AlreadySignedIn.parse(await res.json()) };
  }
  if (!res.ok) throw await errorOf(res);
  return { kind: "signed", session: RedeemInviteResponse.parse(await res.json()) };
}

// ------------------------------------------------ звонок с преподавателями

/**
 * Звонок с преподавателями (`docs/teacher-calls-design.md`, 8.1): урок без
 * ученика, в который преподаватели приходят по ссылке гостя. Ручек пять, и все
 * пять здесь, хотя убирать гостя зовёт только комната: файл один на весь
 * клиент, и второй экземпляр адреса однажды разошёлся бы с первым.
 *
 * ⚠️ **Ключ ссылки едет телом погашения и ответом выдачи, а не в пути.** Путь
 * уезжает в буфер отказов заметок (замаскированным, `maskApiPath`), тело и
 * ответ туда не попадают вовсе. Сам адрес `/call/<id>#<ключ>` держит ключ во
 * фрагменте, и до сервера фрагмент не доходит.
 */

/** Новый звонок. Ответ это строка урока с `call.role = "host"`. Без права 404. */
export function createCall(title: string): Promise<Lesson> {
  const body: CreateCallRequest = { title };
  return call("/v1/calls", { method: "POST", body }, Lesson);
}

/**
 * Ссылка гостя. Только ведущий; повторный запрос отдаёт ту же ссылку, после
 * отзыва новую. POST, потому что первый запрос заводит ключ в базе.
 */
export function requestGuestLink(lessonId: string): Promise<GuestLinkResponse> {
  return call(`/v1/lessons/${lessonId}/guest-link`, { method: "POST" }, GuestLinkResponse);
}

/** Отозвать ссылку гостя: новые не войдут, вошедшие останутся. */
export function revokeGuestLink(lessonId: string): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}/guest-link`, { method: "DELETE" });
}

/**
 * Погашение ссылки гостя вошедшим преподавателем. Сессия при этом не меняется:
 * гость остаётся в своём кабинете, а в ответе только звонок, в который теперь
 * можно войти. Отказы приходят `ApiError`: `call_link_not_found`,
 * `call_link_teacher_only`, `call_full` (в `details.limit` потолок гостей без
 * ведущего), `no_session`, `not_found` у сессии с набором пар.
 */
export function redeemGuestLink(lessonId: string, key: string): Promise<RedeemGuestLinkResponse> {
  const body: RedeemGuestLinkRequest = { key };
  return call(
    `/v1/lessons/${lessonId}/guest-link/redeem`,
    { method: "POST", body },
    RedeemGuestLinkResponse,
  );
}

/**
 * Убрать гостя из звонка. Только ведущий; у уже убранного тоже 204, и отключение
 * делается ещё раз (5.3 проекта). Ни разу не бывший гостем: `participant_not_found`.
 */
export function removeCallGuest(lessonId: string, userId: string): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}/guests/${userId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------- домашка

/**
 * Обвязка домашки живёт здесь: в её ответах нет ни эталонов, ни попыток,
 * только элементы и счётчики, то есть ровно то, что и так лежит в барреле.
 * Проверка, эталоны и попытки домашки живут в ленивом чанке
 * (`features/exercises/answers-api.ts`), по тому же правилу, что урочные:
 * их схемы тянут `@edu/shared/checking`, которому в стартовом чанке не место.
 */

/**
 * Страница списка домашек (4.3 фазы 2).
 *
 * ⚠️ Фильтр состояния СЕРВЕРНЫЙ, а не отбор по приехавшей странице: «просрочено»
 * это про старые работы, а курсор отдаёт свежие, и клиентский фильтр был бы пуст
 * ровно там, где он нужен.
 *
 * ⚠️ Размер страницы выбран против отсечки 16 КБ, а не по ощущению скорости, и
 * потолок его стережёт: `limit` вне границ это отказ, а не усечение.
 */
export function listHomework(query: {
  studentUserId?: string | null;
  state?: HomeworkStateFilter;
  cursor?: string | null;
  limit?: number;
}): Promise<HomeworkListResponse> {
  return call(`/v1/homework${homeworkQuery(query)}`, {}, HomeworkListResponse);
}

/**
 * Сколько работ ученику задали и он их ещё не открывал: источник значка над
 * разделом «Домашка» и полосы на расписании.
 *
 * Своя ручка, а не первая страница списка: значок нужен каркасу кабинета на
 * любом экране, и тащить ради одного числа двадцать пять строк с составом,
 * счётчиками и курсором на канале 128 кбит/с не за что. Считать по приехавшей
 * странице тоже нельзя, она курсорная.
 */
export function getUnopenedHomework(): Promise<UnopenedHomeworkResponse> {
  return call("/v1/homework/unopened", {}, UnopenedHomeworkResponse);
}

/**
 * Корзина: собственный запрос с ТЕМ ЖЕ курсором (4.3 фазы 2).
 *
 * Отдельно от живого списка потому, что страница из двадцати пяти строк могла
 * бы целиком состоять из корзины. С курсором, а не с потолком, потому что
 * отвязка пары кладёт в корзину все домашки пары разом: у пары с годовой
 * историей это сотни строк, то есть гарантированный обрыв на отсечке 16 КБ.
 *
 * Запрос делается лениво, по раскрытию блока: на хабе он лишний, пока корзину
 * не открыли.
 */
export function listHomeworkTrash(query: {
  studentUserId?: string | null;
  cursor?: string | null;
}): Promise<HomeworkListResponse> {
  return call(`/v1/homework${homeworkQuery({ ...query, trash: true })}`, {}, HomeworkListResponse);
}

function homeworkQuery(query: {
  studentUserId?: string | null;
  state?: HomeworkStateFilter;
  cursor?: string | null;
  limit?: number;
  trash?: boolean;
}): string {
  const params = new URLSearchParams();
  if (query.studentUserId != null) params.set("studentUserId", query.studentUserId);
  if (query.state !== undefined && query.state !== "all") params.set("state", query.state);
  if (query.cursor != null) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.trash === true) params.set("trash", "1");
  const s = params.toString();
  return s === "" ? "" : `?${s}`;
}

export function getHomework(homeworkId: string): Promise<HomeworkCard> {
  return call(`/v1/homework/${homeworkId}`, {}, HomeworkCard);
}

export function createHomework(body: CreateHomeworkRequest): Promise<HomeworkCard> {
  return call("/v1/homework", { method: "POST", body }, HomeworkCard);
}

/**
 * Создание с ответом сводкой вместо карточки: единственный вызов цикла
 * мультивыдачи (4.2 docs/homework-phase2-design.md).
 *
 * ⚠️ Это не оптимизация, а разница между работающим и неработающим циклом.
 * Полная карточка со всеми скомпилированными заданиями это около 6 КБ на
 * работу, то есть около 72 КБ на двенадцать, и ещё две с половиной секунды на
 * канале 128 кбит/с сверх кругов по сети. Циклу нужны три поля: id, имя
 * ученика и состояние.
 */
export function createHomeworkSummary(body: CreateHomeworkRequest): Promise<HomeworkSummary> {
  return call("/v1/homework?view=summary", { method: "POST", body }, HomeworkSummary);
}

export function updateHomework(
  homeworkId: string,
  body: UpdateHomeworkRequest,
): Promise<HomeworkCard> {
  return call(`/v1/homework/${homeworkId}`, { method: "PATCH", body }, HomeworkCard);
}

/** Показать разбор. Необратимо; эталоны ученик заберёт своей ручкой сам. */
export function revealHomework(homeworkId: string): Promise<{ revealedAt: number }> {
  return call(
    `/v1/homework/${homeworkId}/reveal`,
    { method: "POST" },
    z.object({ revealedAt: z.number().int() }),
  );
}

export function trashHomework(homeworkId: string): Promise<void> {
  return callVoid(`/v1/homework/${homeworkId}`, { method: "DELETE" });
}

export function restoreHomework(homeworkId: string): Promise<void> {
  return callVoid(`/v1/homework/${homeworkId}/restore`, { method: "POST" });
}

/** Аннулировать задание: аварийный выход при неверном эталоне. Необратимо. */
export function voidHomeworkExercise(
  homeworkId: string,
  elementId: string,
): Promise<{ voidedAt: number }> {
  return call(
    `/v1/homework/${homeworkId}/exercises/${encodeURIComponent(elementId)}/void`,
    { method: "POST" },
    z.object({ voidedAt: z.number().int() }),
  );
}

/**
 * Проверка перевода преподавателем (раздел 4 `docs/translate-review-design.md`).
 *
 * Чтение обеим ролям: преподаватель получает последнюю попытку, черновик и
 * возвращённые, ученик только возвращённые. Схемы из корня `@edu/shared`:
 * ответов и эталонов в них нет, стартовому чанку они безопасны.
 */
export function getHomeworkReviews(homeworkId: string): Promise<HomeworkReviewsResponse> {
  return call(`/v1/homework/${homeworkId}/reviews`, {}, HomeworkReviewsResponse);
}

/**
 * Записать черновик проверки целиком. Отказ `review_stale` несёт текущее
 * состояние в `ApiError.details.current`, разбирает его экран проверки.
 *
 * `keepalive` у записи при уходе со страницы: браузер дошлёт её после выгрузки.
 */
export function saveHomeworkReview(
  homeworkId: string,
  elementId: string,
  body: SaveReviewRequest,
  opts: { keepalive?: boolean } = {},
): Promise<SaveReviewResponse> {
  return call(
    `/v1/homework/${homeworkId}/exercises/${encodeURIComponent(elementId)}/review`,
    { method: "PUT", body, ...(opts.keepalive === true ? { keepalive: true } : {}) },
    SaveReviewResponse,
  );
}

/**
 * Вернуть ученику готовые проверки работы (2.7). Возврат идёт по домашке:
 * вместе с переводами уходит и ответ текстом (1.9
 * `docs/homework-scratch-design.md`). `answer` шлёт экран проверки ответа;
 * отказ по нему (`review_stale`, `review_returned`, `review_answer_erased`)
 * не возвращает ничего, в том числе переводы.
 */
export function returnHomeworkReviews(
  homeworkId: string,
  body: { restOk: { elementId: string; attemptId: string }[]; answer?: ReturnAnswerRequest },
): Promise<ReturnReviewsResponse> {
  return call(
    `/v1/homework/${homeworkId}/reviews/return`,
    { method: "POST", body },
    ReturnReviewsResponse,
  );
}

/**
 * Проверка ответа текстом (1.9 `docs/homework-scratch-design.md`): последний
 * ответ, черновик и прошлая проверка. Только преподавателю.
 */
export function getAnswerReview(homeworkId: string): Promise<AnswerReviewState> {
  return call(`/v1/homework/${homeworkId}/answer/review`, {}, AnswerReviewState);
}

/**
 * Записать черновик проверки ответа целиком. Отказ `review_stale` несёт
 * текущее состояние в `ApiError.details.current`.
 */
export function saveAnswerReview(
  homeworkId: string,
  body: SaveAnswerReviewRequest,
  opts: { keepalive?: boolean } = {},
): Promise<SaveAnswerReviewResponse> {
  return call(
    `/v1/homework/${homeworkId}/answer/review`,
    { method: "PUT", body, ...(opts.keepalive === true ? { keepalive: true } : {}) },
    SaveAnswerReviewResponse,
  );
}

/**
 * Аннулировать задание сразу во всех работах раздачи (4.6 фазы 2). Необратимо.
 *
 * Ключ это позиция задания, а не его идентификатор: у каждого ученика свой
 * `elementId`, но состав раздачи одинаков, значит «то же самое задание» это одна
 * и та же позиция. Правило доступа сервер несёт в самом `UPDATE`: чужая раздача
 * это 404, и число снятых работ в ответе поэтому всегда больше нуля.
 */
export function voidBatchExercise(
  batchId: string,
  position: number,
): Promise<{ voidedWorks: number }> {
  return call(
    `/v1/homework/batch/${encodeURIComponent(batchId)}/exercises/${position}/void`,
    { method: "POST" },
    z.object({ voidedWorks: z.number().int().positive() }),
  );
}

/**
 * Ближайшее не отменённое занятие пары: предзаполнение срока в форме домашки.
 * Сервер считает отмены и переносы; форма про них не знает и знать не должна.
 */
export function nextOccurrence(studentUserId: string): Promise<NextOccurrenceResponse> {
  return call(
    `/v1/schedule/next-occurrence?studentUserId=${encodeURIComponent(studentUserId)}`,
    {},
    NextOccurrenceResponse,
  );
}

// -------------------------------------------------------------- заготовки

export function listPlans(): Promise<PlanListResponse> {
  return call("/v1/plans", {}, PlanListResponse);
}

/**
 * Уроки, собранные по одной заготовке, вместе с самой заготовкой.
 *
 * Отбор делает база по частичному индексу `lesson_plan_idx`, а не браузер по
 * общему списку уроков: у преподавателя с историей их тысячи, и тянуть все ради
 * фильтра по одному полю на канале 128 кбит/с это минуты ожидания.
 */
export function listPlanLessons(planId: string): Promise<PlanLessonsResponse> {
  return call(`/v1/plans/${planId}/lessons`, {}, PlanLessonsResponse);
}

/** Со слайдами. Единственный вызов, который тянет рисунки: их читает редактор. */
export function getPlan(planId: string): Promise<Plan> {
  return call(`/v1/plans/${planId}`, {}, Plan);
}

export function createPlan(title: string): Promise<Plan> {
  return call("/v1/plans", { method: "POST", body: { title } }, Plan);
}

export function renamePlan(planId: string, title: string): Promise<PlanSummary> {
  return call(`/v1/plans/${planId}/rename`, { method: "POST", body: { title } }, PlanSummary);
}

/**
 * Автосохранение редактора: весь список слайдов целиком. POST, а не PUT:
 * список методов в CORS перечислен явно, и PUT в него не входит.
 *
 * `baseRevision` это версия, которую редактор читал. Сервер пишет только поверх
 * неё, и расхождение отвечает `409 plan_stale` вместо молчаливого затирания
 * чужой записи: массив приезжает целиком, и без версии вторая вкладка стирает
 * всё, чего она не видела.
 */
export function savePlanSlides(
  planId: string,
  slides: SlideSnapshot[],
  baseRevision: number,
  /**
   * Источники упражнений: по ним сервер ПЕРЕСОБИРАЕТ элементы второго слоя.
   * Присланным элементам он не верит, и правильно делает: компиляция это
   * единственное место, где ответ отделяется от условия.
   */
  sources: PlanSources,
): Promise<SavePlanSlidesResponse> {
  return call(
    `/v1/plans/${planId}/slides`,
    {
      method: "POST",
      /**
       * `writerFormat` шлём ВСЕГДА, начиная с релиза, который умеет второй
       * слой только читать. Уметь читать здесь и значит уметь писать: редактор
       * отправляет весь массив слайдов целиком, и вкладка, чья схема не знает
       * поля `exercises`, молча выбрасывает его при разборе ответа и стирает
       * упражнения первым же штрихом пера. Число это пропуск, а не версия
       * формата: формат слайдов общий со снимком доски и не двигается.
       */
      body: { slides, baseRevision, sources, writerFormat: PLAN_WRITER_FORMAT },
    },
    /*
      С писателя 5 ответ несёт дописанное под вкладкой (`appendedSlides`), и
      вкладка обязана влить его вместе со сдвигом ревизии: разбор ответа схемой
      без этих полей молча выбросил бы страницы, а следующее сохранение стёрло
      бы их на сервере (docs/pdf-append-design.md, 3.4 п. 4 и п. 8)
    */
    SavePlanSlidesResponse,
  );
}

/**
 * Догоняющее чтение: что дописали в заготовку после ревизии `since`, без
 * записи (docs/pdf-append-design.md, 3.4 п. 7).
 *
 * ⚠️ С тайм-аутом в 10 секунд, в отличие от сохранения: чтение держит тот же
 * замок, что и сохранение, и висящий ответ держал бы им автосохранение. Тайм-аут
 * здесь значит только «не прочитали», ничего не записано.
 */
export function planAppends(planId: string, since: number): Promise<PlanAppendsResponse> {
  return call(`/v1/plans/${planId}/appends?since=${since}`, { timeoutMs: 10_000 }, PlanAppendsResponse);
}

/**
 * Нарисовать график. Рисует сервер, на клиенте ноль килобайт.
 *
 * `scope` едет не ради места, а ради ПРАВ: глобальной роли «преподаватель» в
 * схеме нет вовсе, поэтому рубежей два, и оба про владение. Своя заготовка
 * (отказ `404 plan_not_found`) либо свой урок с нуждой `manage` (отказ
 * `404 lesson_not_found`, а до него ещё `409 entry_closed`). Разбор рубежей у
 * `PlotScope` в `@edu/shared` и в `apps/api/src/routes/plots.ts`.
 *
 * ⚠️ **Частота вызова у двух рубежей РАЗНАЯ, и это решение, а не недоделка**
 * (`docs/math-drawing-design.md`, 3.5). Редактор заготовки зовёт по паузе
 * ввода: там живое превью, и дебаунс обязателен, потому что набор `sin(x`
 * посимвольно это пять промахов кэша подряд, которые забьют очередь глубины
 * четыре и вернут `429 plot_busy` всем остальным. В комнате урока живого
 * превью НЕТ: там рисуют по нажатию, потому что рисователь занимает ядро на
 * той же машине, которая ведёт занятие.
 *
 * ⚠️ **Сервер понимает и СТАРУЮ форму тела (`planId` верхним полем), и это
 * временная ветка, которую отсюда уже не позвать.** Контракт переезжает в три
 * шага (3.1 проекта): открытая вкладка сама не перезагружается, поэтому смена
 * формы одним релизом дала бы `400 invalid_body` каждому, кто в эту минуту
 * собирает заготовку в уже открытой вкладке. Снимает ветку очередь
 * **А1-хвост**, и снимает её ПОСЛЕ выкатки этого файла, а не вместе с ним:
 * пока в живых вкладках крутится прежний бандл, старая форма к ручке приходит.
 * Здесь трогать нечего, работа там (`withScope` и `parseCreatePlot` в
 * `@edu/shared`).
 */
/**
 * Построить график. `probe` это дозаливка подписи старого графика
 * (`docs/graph-formula-label-design.md`, 3.3): на промахе кэша сервер не
 * рисует, а отвечает `409 plot_not_cached`.
 */
export function createPlot(
  scope: PlotScope,
  spec: PlotSpec,
  options: { probe?: boolean } = {},
): Promise<PlotResponse> {
  const body = options.probe === true ? { scope, spec, probe: true } : { scope, spec };
  return call("/v1/plots", { method: "POST", body }, PlotResponse);
}

export function duplicatePlan(planId: string): Promise<PlanSummary> {
  return call(`/v1/plans/${planId}/duplicate`, { method: "POST" }, PlanSummary);
}

/**
 * Заготовку в корзину.
 *
 * `pending` это ответ на вопрос про занятия, которые ещё не взяли её материал.
 * Без него сервер отказывает кодом `plan_has_pending_lessons` и присылает их
 * ЧИСЛО, и больше ничего: решать за человека здесь нельзя, потому что через 30
 * суток корзину чистит уборка, и такое занятие откроется пустым.
 */
export function trashPlan(
  planId: string,
  pending?: "materialize" | "trash",
): Promise<void> {
  const query = pending === undefined ? "" : `?pending=${pending}`;
  return callVoid(`/v1/plans/${planId}${query}`, { method: "DELETE" });
}

export function restorePlan(planId: string): Promise<PlanSummary> {
  return call(`/v1/plans/${planId}/restore`, { method: "POST" }, PlanSummary);
}

// -------------------------------------------------------------- материалы

export function listMedia(folderId: string | null): Promise<MediaListResponse> {
  const q = folderId === null ? "" : `?folderId=${folderId}`;
  return call(`/v1/media${q}`, {}, MediaListResponse);
}

/**
 * Только квота, без списка файлов и дерева папок.
 *
 * Ради полоски хранилища в сайдбаре: он висит на всех экранах кабинета, а
 * `listMedia` ради тех же двух чисел тянет всё дерево и содержимое корня.
 */
export function mediaQuota(): Promise<MediaQuota> {
  return call("/v1/media/quota", {}, MediaQuota);
}

/**
 * Кредиты ИИ месяца: сколько занято и сколько осталось.
 *
 * Ради полоски в боковой панели, соседа хранилища, и по той же причине лёгкой
 * ручкой: сайдбар висит на всех экранах кабинета, и два числа не должны стоить
 * ни одного лишнего чтения.
 *
 * ⚠️ **Ученику ручка отвечает 404**, поэтому зовётся она только с
 * преподавательской стороны. Отказ полоска проглатывает молча: она справка, а не
 * работа.
 *
 * ⚠️ **Схема лёгкая нарочно** (`@edu/shared/ai-credits.ts`): её читает
 * СТАРТОВЫЙ чанк, и ни цены озвучки, ни разреза админской панели за ней не
 * едет.
 */
export function aiCredits(): Promise<AiCreditsResponse> {
  return call("/v1/ai/credits", {}, AiCreditsResponse);
}

export function listMediaTrash(): Promise<TrashListResponse> {
  return call("/v1/media/trash", {}, TrashListResponse);
}

export function listTrashGroup(groupId: string): Promise<TrashGroupContents> {
  return call(`/v1/media/trash/groups/${groupId}`, {}, TrashGroupContents);
}

export function restoreTrashGroup(groupId: string): Promise<RestoreReport> {
  return call(`/v1/media/trash/groups/${groupId}/restore`, { method: "POST" }, RestoreReport);
}

export function purgeTrashGroup(groupId: string): Promise<void> {
  return callVoid(`/v1/media/trash/groups/${groupId}`, { method: "DELETE" });
}

/**
 * Пакетные операции: один запрос на всё выделенное.
 *
 * Десять запросов по очереди на плохом канале это десять шансов оборваться
 * посередине, а пакет либо применяется целиком, либо не применяется вовсе.
 */
export function trashMediaItems(items: {
  fileIds: string[];
  folderIds: string[];
}): Promise<TrashBatchResult> {
  return call("/v1/media/trash", { method: "POST", body: items }, TrashBatchResult);
}

export function moveMediaItems(items: {
  fileIds: string[];
  folderIds: string[];
  folderId: string | null;
}): Promise<MediaBatchResult> {
  return call("/v1/media/move", { method: "POST", body: items }, MediaBatchResult);
}

/** Где файл стоит: заготовки и уроки. Показывается в свойствах. */
export function mediaFileUsage(fileId: string): Promise<MediaUsage> {
  return call(`/v1/media/files/${fileId}/usage`, {}, MediaUsage);
}

/**
 * Где стоит всё выделенное. Один запрос на пакет, а не по запросу на плитку:
 * десять кругов по сети перед показом диалога это замерший интерфейс на слабом
 * канале. Папки сервер раскрывает в поддерево сам.
 */
export function mediaUsageBatch(items: {
  fileIds: string[];
  folderIds: string[];
}): Promise<MediaUsageDigest> {
  return call("/v1/media/usage", { method: "POST", body: items }, MediaUsageDigest);
}

/**
 * Кадр для плитки. Мимо общего `request`: тело двоичное, а не JSON.
 *
 * Неудача здесь не ломает загрузку: файл уже лежит на сервере, а плитка без
 * картинки это плитка со значком, а не потерянный файл.
 */
export async function uploadMediaPreview(
  fileId: string,
  image: Blob,
): Promise<MediaPreviewResponse> {
  const cfg = await loadRuntimeConfig();
  const res = await fetch(`${cfg.apiBaseUrl}/v1/media/files/${fileId}/preview`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/octet-stream" },
    body: image,
  });
  if (res.status === 401) onUnauthorized?.();
  if (!res.ok) throw await errorOf(res);
  return MediaPreviewResponse.parse(await res.json());
}

/**
 * Картинка, брошенная на доску урока. Байты уже ужаты браузером.
 *
 * Мимо общего `request` по той же причине, что и превью: тело двоичное, а не
 * JSON. Размер приезжает строкой запроса, потому что его посчитал тот же
 * браузер, который только что нарисовал канвас, а декодировать картинку на
 * сервере ради двух чисел незачем.
 *
 * ⚠️ Ручка УЗКАЯ и живёт отдельно от медиатеки намеренно: ею пользуется и
 * ученик, у которого своей библиотеки нет вовсе, и переиспользовать здесь общий
 * загрузчик значило бы выдать ему весь медийный API (шапка
 * `apps/api/src/routes/board-images.ts`).
 */
export function uploadBoardImage(
  lessonId: string,
  image: Blob,
  size: { width: number; height: number },
): Promise<BoardImageResponse> {
  return sendBoardImage(`/v1/lessons/${lessonId}/board-image`, image, size);
}

/**
 * То же самое для доски ЗАГОТОВКИ.
 *
 * Отдельный адрес, а не параметр: права там считаются от владельца заготовки, а
 * не от пары «преподаватель и ученик», и счётчика картинок у неё нет вовсе.
 * Общая половина обеих отправок ровно одна, и она ниже.
 *
 * `name` передаёт окно «Текст с картинки» (07.10.2026): имя выбранного или
 * брошенного файла без расширения, у скриншота «Скриншот <дата>». Без него сервер называет
 * файл по времени («Доска 07.10.2026 22:24.png»), как картинку, брошенную на
 * доску. Расширение ставит сервер по содержимому, чистит имя тоже он.
 */
export function uploadPlanBoardImage(
  planId: string,
  image: Blob,
  size: { width: number; height: number },
  name?: string,
  /** Отмена загрузки: вырезки скана грузятся с потолком ожидания (`scan-pictures-cut.ts`). */
  signal?: AbortSignal,
): Promise<BoardImageResponse> {
  return sendBoardImage(`/v1/plans/${planId}/board-image`, image, size, name, signal);
}

function sendBoardImage(
  path: string,
  image: Blob,
  size: { width: number; height: number },
  name?: string,
  signal?: AbortSignal,
): Promise<BoardImageResponse> {
  return sendBytes(path, image, size, BoardImageResponse, name, signal);
}

/**
 * Приложить фотографию решения ко всей домашней работе
 * (`docs/homework-photo-design.md`).
 *
 * Тем же сырым телом и тем же отправителем, что картинка на доску: у ученика
 * нет медиатеки, и общий загрузчик ему давать нельзя. Отличаются только адрес и
 * потолки ужатия, а ужимает вызывающий (`encodeBoardImage` с числами домашки).
 *
 * ⚠️ Задания в адресе нет: с 05.09.2026 фотография привязана к работе целиком.
 */
export function uploadHomeworkPhoto(
  homeworkId: string,
  image: Blob,
  size: { width: number; height: number },
): Promise<HomeworkPhotoResponse> {
  return sendBytes(`/v1/homework/${homeworkId}/photo`, image, size, HomeworkPhotoResponse);
}

/** Убрать свою фотографию. Файл при этом удаляется насовсем. */
export function deleteHomeworkPhoto(homeworkId: string, fileId: string): Promise<void> {
  return callVoid(`/v1/homework/${homeworkId}/photos/${fileId}`, { method: "DELETE" });
}

/**
 * Отметить фотографию просмотренной. Зовёт ОТКРЫТИЕ КРУПНО, а не отрисовка
 * миниатюры: иначе отметка соврала бы в первый же день, пометив просмотренным
 * всё, мимо чего человек проскроллил (раздел 4 проекта).
 */
export function markHomeworkPhotoViewed(
  homeworkId: string,
  fileId: string,
): Promise<HomeworkPhotoViewedResponse> {
  return call(
    `/v1/homework/${homeworkId}/photos/${fileId}/viewed`,
    { method: "POST" },
    HomeworkPhotoViewedResponse,
  );
}

/**
 * «Сделал(а)» у домашки без заданий (1.5 `docs/homework-scratch-design.md`).
 * Идемпотентна: повтор после обрыва момент не двигает, поэтому клиент вправе
 * повторять нажатие сколько угодно.
 */
export function markHomeworkDone(homeworkId: string): Promise<HomeworkDoneResponse> {
  return call(`/v1/homework/${homeworkId}/done`, { method: "POST" }, HomeworkDoneResponse);
}

/**
 * Снять отметку «Сделал(а)». ⚠️ Отвечает ТЕКУЩИМ значением: после срока или при
 * приложенном фото отметка окончательна, и ручка вернёт прежний момент с 200.
 * Экран рисуется от ответа, а не от того, что просил.
 */
export function unmarkHomeworkDone(homeworkId: string): Promise<HomeworkDoneResponse> {
  return call(`/v1/homework/${homeworkId}/done`, { method: "DELETE" }, HomeworkDoneResponse);
}

/**
 * Ответ ученика текстом (1.8 `docs/homework-scratch-design.md`). Заменяет
 * прошлый целиком; первая отправка пишет отметку «сделано», повтор её не
 * двигает, поэтому клиент вправе повторять после обрыва.
 */
export function sendHomeworkAnswer(
  homeworkId: string,
  text: string,
): Promise<HomeworkAnswerResponse> {
  return call(
    `/v1/homework/${homeworkId}/answer`,
    { method: "PUT", body: { text } },
    HomeworkAnswerResponse,
  );
}

/**
 * Преподаватель открыл результаты с ответом текстом: ответ больше не «новый».
 * Идемпотентна. Отмечается только увиденный ответ, не новее `answerAt`.
 */
export function markHomeworkAnswerViewed(
  homeworkId: string,
  /** Момент ответа, который преподаватель видит на экране. */
  answerAt: number,
): Promise<HomeworkAnswerViewedResponse> {
  return call(
    `/v1/homework/${homeworkId}/answer/viewed`,
    { method: "POST", body: { answerAt } },
    HomeworkAnswerViewedResponse,
  );
}

/** «Сохранить картинки как заготовку» (1.7 проекта): слайд на картинку. */
export function saveHomeworkAsPlan(homeworkId: string): Promise<HomeworkSaveAsPlanResponse> {
  return call(
    `/v1/homework/${homeworkId}/save-as-plan`,
    { method: "POST" },
    HomeworkSaveAsPlanResponse,
  );
}

/** Картинки урока, которые можно взять в материал домашки («С доски урока»). */
export function listLessonImages(lessonId: string): Promise<SourceImagesResponse> {
  return call(`/v1/lessons/${lessonId}/images`, {}, SourceImagesResponse);
}

/** Картинки заготовки, которые можно взять в материал домашки. */
export function listPlanImages(planId: string): Promise<SourceImagesResponse> {
  return call(`/v1/plans/${planId}/images`, {}, SourceImagesResponse);
}

// ------------------------------------------------------------------ задания из PDF

/**
 * Последний разбор заготовки и окно запуска (раздел 9
 * docs/exercise-extraction-design.md). `pages` приезжают только без живой
 * задачи: пока разбор идёт, окна запуска нет.
 *
 * Путь картинки (раздел 12 docs/scan-extraction-design.md) читается той же
 * схемой из `@edu/shared`, второго описания полей здесь нет: в корне
 * `scanCreditsPerPage` (оценка кредитов на страницу скана), у страницы окна
 * запуска `via`, `sourceFileId`, `sourceName`, `pageNo`, `confirmed`, у
 * страницы задачи `via`, `pageNo`, `pageLabel`, `read`, `passages`, `scanIds`,
 * `rejectedTasks` и `scan`. ⚠️ `scan` приезжает НЕРАЗОБРАННЫМ (`unknown[]`): в
 * нём ответы модели, а этот модуль лежит в стартовом чанке; разбирает его
 * ленивый `features/exercises/scan-place.ts`.
 */
export function getLatestExtraction(planId: string): Promise<ExtractLatestResponse> {
  /*
    С `quietFail`, как у соседних фоновых счётчиков: это опрос кнопки, а не
    работа человека. Без ручек (выключатель `EXTRACT_ENABLED` на сервере) каждая
    открытая заготовка писала бы 404 в кольцевой буфер и выносила из него
    отказы, ради которых человек откроет окно заметки. Отказ проглатывает
    вызывающий: кнопки тогда нет, сказать о нём нечего.
  */
  return call(`/v1/plans/${planId}/extractions/latest`, { quietFail: true }, ExtractLatestResponse);
}

/**
 * Запуск разбора. Тот же `clientKey` отдаёт ту же задачу (200 вместо 201), то
 * есть повтор запроса после обрыва связи второго разбора не заводит.
 *
 * `confirmSources` это исходные файлы, которые преподаватель отметил галочкой
 * «В этом файле учебный материал по английскому языку, работ учеников в нём
 * нет» (решение 20 docs/scan-extraction-design.md). Сервер пишет подтверждение
 * той же транзакцией, что и запуск, и только потом решает `extract_nothing`;
 * чужой uuid не пишется. Без галочки поле не посылается.
 */
export function startExtraction(planId: string, body: ExtractStartRequest): Promise<ExtractJobResponse> {
  return call(`/v1/plans/${planId}/extractions`, { method: "POST", body }, ExtractJobResponse);
}

/** Повтор одной страницы последнего разбора. */
export function retryExtraction(planId: string, jobId: string, slideId: string): Promise<ExtractJobResponse> {
  return call(
    `/v1/plans/${planId}/extractions/${jobId}/slides/${encodeURIComponent(slideId)}/retry`,
    { method: "POST" },
    ExtractJobResponse,
  );
}

/** Потолок одной отправки картинки материала, миллисекунды. */
const MATERIAL_UPLOAD_TIMEOUT_MS = 120_000;

/**
 * «Добавить фото» в форме выдачи домашки (1.2 проекта, источник 1). Байты уже
 * ужаты вызывающим (`encodeBoardImage` с потолками фото домашки).
 *
 * ⚠️ Через `XMLHttpRequest`, а не `fetch`, и это единственная причина, по
 * которой отправка не идёт через `sendBytes`: у `fetch` нет прогресса отправки,
 * а картинка в 800 КБ на 128 кбит/с едет почти минуту, и минута без движения
 * читается как зависание. Разбор отказа при этом ОБЩИЙ: ответ заворачивается в
 * `Response` и уходит в тот же `errorOf`, иначе вторая копия разбора разъехалась
 * бы с первой.
 */
export async function uploadHomeworkMaterialImage(
  image: Blob,
  size: { width: number; height: number },
  opts: {
    onProgress?: (fraction: number) => void;
    /** «Убрать» и уход с формы: запрос обрывается, очередь не ждёт его. */
    signal?: AbortSignal;
  } = {},
): Promise<HomeworkMaterialUploadResponse> {
  const { onProgress, signal } = opts;
  const cfg = await loadRuntimeConfig();
  const url = `${cfg.apiBaseUrl}/v1/homework/material-image?w=${size.width}&h=${size.height}`;
  const { status, text } = await new Promise<{ status: number; text: string }>(
    (resolve, reject) => {
      if (signal?.aborted === true) {
        reject(new DOMException("aborted", "AbortError"));
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      /*
        Потолок на картинку. 800 КБ при 128 кбит/с это около минуты, двойной
        запас покрывает дрожание канала; дальше это уже не медленно, а
        зависло, и человеку нужна кнопка «Повторить», а не вечная полоса
      */
      xhr.timeout = MATERIAL_UPLOAD_TIMEOUT_MS;
      const onAbort = (): void => xhr.abort();
      signal?.addEventListener("abort", onAbort, { once: true });
      xhr.onloadend = () => signal?.removeEventListener("abort", onAbort);
      xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", "application/octet-stream");
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
      };
      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
      // Обрыв это `TypeError`, как у `fetch`: вызывающие отличают сеть от отказа
      // по классу ошибки, и второй класс здесь сломал бы им это различение
      xhr.onerror = () => reject(new TypeError("network error"));
      xhr.ontimeout = () => reject(new TypeError("network timeout"));
      xhr.send(image);
    },
  );
  if (status === 401) onUnauthorized?.();
  if (status < 200 || status >= 300) {
    throw await errorOf(new Response(text, { status }));
  }
  try {
    return HomeworkMaterialUploadResponse.parse(JSON.parse(text));
  } catch (e) {
    throw new ResponseFormatError("/v1/homework/material-image", e);
  }
}

/**
 * Отправка сырого тела: одна на все узкие загрузки картинок.
 *
 * Общий `call` сюда не годится вовсе: он шлёт JSON, а здесь тело это байты.
 * Общая же она потому, что каждая копия несла бы свой набор заголовков и свою
 * обработку 401, и они уже разъезжались бы.
 */
async function sendBytes<T>(
  path: string,
  image: Blob,
  size: { width: number; height: number },
  schema: { parse: (v: unknown) => T },
  name?: string,
  /** Отмена обрывает запрос: тело, не доехавшее до конца, сервер файлом не сохранит. */
  signal?: AbortSignal,
): Promise<T> {
  const cfg = await loadRuntimeConfig();
  const named = name === undefined || name === "" ? "" : `&name=${encodeURIComponent(name)}`;
  const query = `?w=${size.width}&h=${size.height}${named}`;
  const res = await fetch(`${cfg.apiBaseUrl}${path}${query}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/octet-stream" },
    body: image,
    ...(signal === undefined ? {} : { signal }),
  });
  if (res.status === 401) onUnauthorized?.();
  if (!res.ok) throw await errorOf(res);
  return schema.parse(await res.json());
}

/** Полный адрес превью из относительного пути, который отдал сервер. */
export async function mediaAssetUrl(path: string): Promise<string> {
  const cfg = await loadRuntimeConfig();
  return `${cfg.apiBaseUrl}${path}`;
}

export function createFolder(name: string, parentId: string | null): Promise<MediaFolder> {
  return call("/v1/media/folders", { method: "POST", body: { name, parentId } }, MediaFolder);
}

export function renameFolder(folderId: string, name: string): Promise<MediaFolder> {
  return call(`/v1/media/folders/${folderId}/rename`, { method: "POST", body: { name } }, MediaFolder);
}

export function moveFolder(folderId: string, parentId: string | null): Promise<MediaFolder> {
  return call(`/v1/media/folders/${folderId}/move`, { method: "POST", body: { parentId } }, MediaFolder);
}

export function deleteFolder(folderId: string): Promise<void> {
  return callVoid(`/v1/media/folders/${folderId}`, { method: "DELETE" });
}

export function renameMediaFile(fileId: string, name: string): Promise<MediaFile> {
  return call(`/v1/media/files/${fileId}/rename`, { method: "POST", body: { name } }, MediaFile);
}

export function moveMediaFile(fileId: string, folderId: string | null): Promise<MediaFile> {
  return call(`/v1/media/files/${fileId}/move`, { method: "POST", body: { folderId } }, MediaFile);
}

export function restoreMediaFile(fileId: string): Promise<MediaFile> {
  return call(`/v1/media/files/${fileId}/restore`, { method: "POST" }, MediaFile);
}

export function purgeMediaFile(fileId: string): Promise<void> {
  return callVoid(`/v1/media/files/${fileId}/purge`, { method: "DELETE" });
}

/**
 * Подписанная ссылка на содержимое, уже пригодная для `<video src>`.
 *
 * Сервер отдаёт относительный путь, базовый адрес API добавляется здесь: на
 * проде SPA и API живут на одном имени, а в деве на разных портах, и зашивать
 * это знание в ответ сервера значило бы завести переменную окружения, которая
 * умеет разъехаться с реальностью.
 */
export async function mediaFileUrl(fileId: string): Promise<{ url: string; expiresAt: number }> {
  const link = await call(`/v1/media/files/${fileId}/link`, { method: "POST" }, MediaLinkResponse);
  const cfg = await loadRuntimeConfig();
  return { url: `${cfg.apiBaseUrl}${link.path}`, expiresAt: link.expiresAt };
}

export function createUpload(args: {
  name: string;
  contentType: string;
  sizeBytes: number;
  folderId: string | null;
}): Promise<UploadSession> {
  return call("/v1/media/uploads", { method: "POST", body: args }, UploadSession);
}

export function getUpload(uploadId: string): Promise<UploadSession> {
  return call(`/v1/media/uploads/${uploadId}`, {}, UploadSession);
}

/**
 * Незавершённые загрузки. Отдельный запрос, а не поле в списке материалов:
 * список папки перечитывается на каждый чих (переименовал, переместил, удалил),
 * а недокачанные меняются только при загрузке.
 */
export function listPendingUploads(): Promise<PendingUploadListResponse> {
  return call("/v1/media/uploads", {}, PendingUploadListResponse);
}

/** Отпечаток уже принятой части. Нужен только при продолжении чужим файлом. */
export function uploadFingerprint(uploadId: string): Promise<UploadFingerprint> {
  return call(`/v1/media/uploads/${uploadId}/fingerprint`, {}, UploadFingerprint);
}

export function cancelUpload(uploadId: string): Promise<void> {
  return callVoid(`/v1/media/uploads/${uploadId}`, { method: "DELETE" });
}

export function completeUpload(
  uploadId: string,
  meta: { durationMs?: number; width?: number; height?: number },
): Promise<MediaFile> {
  return call(`/v1/media/uploads/${uploadId}/complete`, { method: "POST", body: meta }, MediaFile);
}

/**
 * Кусок загрузки. Мимо общего `request`, потому что тело здесь двоичное, а не
 * JSON, и нужен `signal` для отмены.
 *
 * 409 это не поломка, а нормальный ответ «у меня другое смещение»: сервер
 * присылает своё, и клиент продолжает с него. Поэтому он разбирается здесь, а
 * не превращается в исключение.
 */
export async function sendUploadChunk(args: {
  uploadId: string;
  offset: number;
  chunk: Blob;
  signal: AbortSignal;
}): Promise<UploadSession | { mismatch: number }> {
  const cfg = await loadRuntimeConfig();
  const res = await fetch(
    `${cfg.apiBaseUrl}/v1/media/uploads/${args.uploadId}?offset=${args.offset}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/octet-stream" },
      body: args.chunk,
      signal: args.signal,
    },
  );

  if (res.status === 409) {
    const body = (await res.json()) as { offset?: unknown };
    return { mismatch: typeof body.offset === "number" ? body.offset : 0 };
  }
  if (res.status === 401) onUnauthorized?.();
  if (!res.ok) throw await errorOf(res);
  return UploadSession.parse(await res.json());
}

// ------------------------------------------------------------------- книга

/*
  PDF в «Материалах», открытый книгой (docs/pdf-book-view-design.md, 4.2).
  Состояние считает сервер (`bookStateOf`), ответ несёт его как есть.
*/

/** Книга и «сейчас» сервера (`callDated`). */
export interface DatedBook {
  book: MediaBook;
  serverNow: number | null;
}

async function datedBook(path: string, init: Init): Promise<DatedBook> {
  const got = await callDated(path, init, MediaBook);
  return { book: got.value, serverNow: got.serverNow };
}

/**
 * Срок ответа книги. Без него зависший запрос держал бы экран: опрос ждёт
 * ответа перед следующим шагом, а висящий POST не пускает чтения (их ответы
 * отбрасываются, пока POST в пути). Ручки отвечают без ожидания pdfium.
 */
const BOOK_TIMEOUT_MS = 15_000;

/** Книга: страницы, состояние и подписи на картинки. Задания не заводит никогда. */
export function getBook(fileId: string): Promise<DatedBook> {
  return datedBook(`/v1/media/files/${fileId}/pages`, { timeoutMs: BOOK_TIMEOUT_MS });
}

/**
 * «Начать или повторить» (Р8): при `none` сервер заводит подготовку страниц,
 * при временной неудаче снимает её и пересчитывает. Ответ это книга после
 * действия. Под просмотром кабинета отвечает `view_only`, поэтому экран книги
 * там её не зовёт.
 */
export function startBookProbe(fileId: string): Promise<DatedBook> {
  return datedBook(`/v1/media/files/${fileId}/pages`, { method: "POST", timeoutMs: BOOK_TIMEOUT_MS });
}

/**
 * Путь картинки страницы книги: миниатюра (`thumb`) или крупная (`view`), с
 * подписью из ответа книги. Полный адрес даёт `mediaAssetUrl` или его база.
 */
export function bookPagePath(fileId: string, pageNo: number, kind: "thumb" | "view", token: string): string {
  return `/v1/media/files/${fileId}/pages/${pageNo}/${kind}?t=${encodeURIComponent(token)}`;
}

// ----------------------------------------------------------------- импорт

/**
 * Вход в нарезку документа. Он же повторный вход: если по этому файлу уже висит
 * незавершённое задание, сервер отдаёт ЕГО, а не создаёт второе. Человек,
 * вернувшийся к брошенному экрану, увидит свои миниатюры и свои галочки там,
 * где их оставил.
 *
 * `targetPlanId` это цель дописывания (docs/pdf-append-design.md, 3.9):
 * побеждает последнее намерение, поэтому вход без цели снимает её с задания,
 * ещё не ушедшего в работу. Режим экрана берётся из ответа, а не из этого
 * аргумента.
 */
export function startImport(fileId: string, targetPlanId: string | null = null): Promise<Dated<ImportJobStatus>> {
  // С «сейчас» сервера: экран выбора держит по нему подпись миниатюр (Р14)
  return callDated("/v1/import/jobs", { method: "POST", body: { fileId, targetPlanId } }, ImportJobStatus);
}

/**
 * Состояние задания. Опрашивается раз в полторы-две секунды, а не приезжает по
 * WebSocket: долгоживущий WebSocket на домене API запрещён разделом 4 CLAUDE.md,
 * а тащить прогресс в `edu-ws` значило бы уронить импорт вместе с доской.
 */
export function getImportJob(
  jobId: string,
  opts?: { timeoutMs?: number },
): Promise<ImportJobStatus> {
  return call(
    `/v1/import/jobs/${jobId}`,
    opts?.timeoutMs === undefined ? {} : { timeoutMs: opts.timeoutMs },
    ImportJobStatus,
  );
}

/** Состояние задания с «сейчас» сервера: опрос экрана выбора страниц (Р14). */
export function readImportJob(jobId: string): Promise<Dated<ImportJobStatus>> {
  return callDated(`/v1/import/jobs/${jobId}`, {}, ImportJobStatus);
}

export function chooseImportPages(
  jobId: string,
  /**
   * `targetPlanId` это ОЖИДАЕМАЯ цель, та, что видел экран: сервер сверяет её
   * под замком задания и при расхождении отвечает `job_target_changed` (3.9).
   */
  choice: { pages: number[]; title: string; splitEvery: number | null; targetPlanId: string | null },
): Promise<ImportJobStatus> {
  return call(`/v1/import/jobs/${jobId}/pages`, { method: "POST", body: choice }, ImportJobStatus);
}

export function cancelImport(jobId: string): Promise<ImportJobStatus> {
  return call(`/v1/import/jobs/${jobId}/cancel`, { method: "POST" }, ImportJobStatus);
}

// ------------------------------------------------- материалы на доску урока

/*
  Ручки вставки из «Материалов» на доску урока
  (`docs/room-material-insert-design.md`, разделы 3.4 и 4). Задание то же, что у
  экрана нарезки, только с целью-уроком; зовёт их хук комнаты
  (`board/use-material-insert.ts`) и окно выбора (`board/RoomMaterialDialog.tsx`).
  `lessonId` везде это урок, из которого открыта комната: сервер сверяет его с
  уроками комнаты, а не с одним им, поэтому второе устройство преподавателя,
  вошедшее через другой урок группы, ведёт те же задания.
*/

/**
 * Тайм-аут ручек вставки. Исключение из правила «по умолчанию его нет» (разбор
 * у `Init.timeoutMs`), и причина в том, кто ждёт ответа: хук комнаты ведёт
 * вставку цепочкой, где следующий запрос рождается из ответа на предыдущий.
 * Запрос, повисший под отсечкой, остановил бы цепочку навсегда, и строка над
 * слайдом стояла бы «Готовим страницы» до конца урока. Оборванный по
 * тайм-ауту он становится обрывом сети, а обрыв автомат переживает повтором.
 * Тела здесь крошечные, 20 секунд с запасом хватает и на 128 кбит/с.
 */
export const ROOM_MATERIAL_TIMEOUT_MS = 20_000;

/**
 * Вход в нарезку для доски урока. Живое задание по этому PDF до выбора страниц
 * переназначается на урок; уже ушедшее в очередь для этой же комнаты отдаётся
 * как есть (окно подхватывает его ход), для заготовки или другого урока это
 * `409 job_not_ready` с `details.for`.
 */
export function startLessonImport(fileId: string, lessonId: string): Promise<ImportJobStatus> {
  return call(
    "/v1/import/jobs",
    {
      method: "POST",
      body: { fileId, targetLessonId: lessonId },
      timeoutMs: ROOM_MATERIAL_TIMEOUT_MS,
    },
    ImportJobStatus,
  );
}

/**
 * Выбор страниц задания урока. `pages: null` это одна пометка «выбор ждёт»
 * якорем (3.7): поле страниц тогда не едет вовсе, как того ждёт схема запроса.
 */
export function chooseLessonPages(
  jobId: string,
  choice: { lessonId: string; pages: readonly number[] | null; anchorSlideId: string },
): Promise<ImportJobStatus> {
  const body = {
    targetLessonId: choice.lessonId,
    anchorSlideId: choice.anchorSlideId,
    ...(choice.pages === null ? {} : { pages: [...choice.pages] }),
  };
  return call(
    `/v1/import/jobs/${jobId}/pages`,
    { method: "POST", body, timeoutMs: ROOM_MATERIAL_TIMEOUT_MS },
    ImportJobStatus,
  );
}

/**
 * Отмена задания урока, три режима (3.4). `beforeChoice` с `unmarked` у «Назад»
 * и закрытия окна на шаге страниц: гасит задание до выбора, только если на нём
 * нет пометки «выбор ждёт». `beforeChoice` без `unmarked` только у крестика
 * строки «ещё не выбраны»: гасит задание до выбора и с пометкой. Без
 * `beforeChoice` живое отменяется, а готовое и упавшее убираются из
 * восстановления.
 */
export function cancelLessonImport(
  jobId: string,
  lessonId: string,
  beforeChoice: boolean,
  /**
   * `unmarked` у «Назад» и закрытия окна на шаге страниц: сервер гасит задание
   * до выбора, только если на нём ещё нет пометки «выбор ждёт», в той же записи.
   * Задание с пометкой (его выбрали в другой вкладке) отдаётся как есть.
   * Только вместе с `beforeChoice`.
   */
  opts?: { unmarked?: boolean },
): Promise<ImportJobStatus> {
  const unmarked = beforeChoice && opts?.unmarked === true;
  return call(
    `/v1/import/jobs/${jobId}/cancel`,
    {
      method: "POST",
      body: { targetLessonId: lessonId, beforeChoice, ...(unmarked ? { unmarked: true } : {}) },
      timeoutMs: ROOM_MATERIAL_TIMEOUT_MS,
    },
    ImportJobStatus,
  );
}

/** Право ученикам комнаты на файлы до их появления на доске (3.5). Ответ 204, повтор безопасен. */
export function grantBoardFiles(lessonId: string, body: BoardFilesRequest): Promise<void> {
  return callVoid(`/v1/lessons/${lessonId}/board-files`, {
    method: "POST",
    body,
    timeoutMs: ROOM_MATERIAL_TIMEOUT_MS,
  });
}

/** Задания этой комнаты для восстановления после перезагрузки вкладки (3.7). */
export function listMaterialJobs(lessonId: string): Promise<MaterialJobsResponse> {
  return call(
    `/v1/lessons/${lessonId}/material-jobs`,
    { timeoutMs: ROOM_MATERIAL_TIMEOUT_MS },
    MaterialJobsResponse,
  );
}

// -------------------------------------------------------------- телеметрия

/**
 * Батч телеметрии ICE. Ответ (202 и счётчик) клиенту не нужен: он ничего с
 * ним не делает, а неудача отправки не должна быть видна на уроке.
 */
export async function sendTelemetry(batch: TelemetryBatch, keepalive: boolean): Promise<void> {
  await request("/v1/telemetry/webrtc", { method: "POST", body: batch, keepalive });
}

/**
 * Замер цикла раздачи домашки (5.12 docs/homework-phase2-design.md).
 *
 * Ответа нет и не нужно: у отложенного решения про серверную пачку появляется
 * прибор, а неудача отправки прибора не должна быть видна тому, кто только что
 * задал домашку двенадцати ученикам. Поэтому единственный вызывающий её код
 * глушит отказ.
 */
export async function sendHomeworkBatchEvent(event: HomeworkBatchEvent): Promise<void> {
  await request("/v1/telemetry/homework-batch", { method: "POST", body: event });
}

/**
 * Шаги входа в урок и результат предполётной проверки (раздел 5.5
 * `docs/room-preflight-design.md`).
 *
 * ⚠️ `silent401: true` обязателен. Ручка принимает событие БЕЗ сессии
 * намеренно: сценарий, который её породил, это вход ученика, у которого
 * аккаунта нет вовсе. Без флага общий обработчик `401` выкинул бы такого
 * человека на форму входа ровно в ту секунду, когда он входит в урок.
 *
 * Тайм-аут короткий и жёсткий: прибор не имеет права держать соединение
 * дольше, чем то, что он измеряет. Отказ отправки глушит вызывающий.
 */
export async function sendEntryTelemetry(event: EntryTelemetryEvent): Promise<void> {
  await request("/v1/telemetry/entry", {
    method: "POST",
    body: event,
    silent401: true,
    timeoutMs: 10_000,
  });
}

// ------------------------------------------------- заметки о неточностях

/**
 * Отправка заметки о неточности (`docs/feedback-notes-design.md`, раздел 6.5).
 *
 * ⚠️ **Не бросает, а возвращает статус**, и это не небрежность, а требование
 * очереди. Очередь разбирает ответ ПО HTTP-СТАТУСУ: `400` и `403` это
 * постоянные неудачи, а `401`, `404`, `429` и `5xx` временные, каждая со своим
 * поведением. Разбор по `ApiError.code` был бы неверен: отказ по лимиту частоты
 * приезжает в браузер как `bad_request`, а не `rate_limited` (лимитёр Fastify не
 * несёт нашего поля `error`), то есть `429` попал бы в ветку постоянных отказов
 * и заметка помечалась бы безнадёжной ровно на том лимите, вокруг которого
 * написана вся очередь. Ноль означает «до сервера не дошло»: обрыв сети или
 * недоступный API, для очереди это такая же временная неудача.
 *
 * ⚠️ **`silent401: true` здесь блокирующее требование, а не оптимизация.** До
 * сих пор у флага был ровно один потребитель, `getMe()` при старте вкладки, где
 * `401` это нормальный ответ. Теперь потребителей два, и у второго причина
 * жёстче: `request()` на любом `401` без флага зовёт общий `onUnauthorized`,
 * который сбрасывает сессию на экран входа. Очередь работает фоновым таймером,
 * то есть без флага истёкшая или отозванная сессия выкидывала бы человека на
 * форму входа посреди урока, причём в момент, который он никак не связал бы со
 * своим действием.
 *
 * `keepalive` нужен ровно одному вызывающему: досылке очереди на `pagehide`, где
 * вкладка уже выгружается (см. `main.tsx`). На кнопке «Выйти» он не нужен, там
 * вкладка живёт, и доставкой занимается `flushBeforeSignOut` из `session.tsx`.
 */
export async function postFeedback(
  body: CreateFeedbackNoteRequest,
  opts?: { keepalive?: boolean },
): Promise<number> {
  try {
    const res = await request("/v1/feedback", {
      method: "POST",
      body,
      silent401: true,
      // Условным разворотом, а не `keepalive: opts?.keepalive`: при
      // exactOptionalPropertyTypes `undefined` в необязательное поле не кладётся
      ...(opts?.keepalive === true ? { keepalive: true } : {}),
    });
    return res.status;
  } catch (e) {
    // Ответ с кодом отказа несёт статус, сетевая ошибка не несёт ничего
    return e instanceof ApiError ? e.status : 0;
  }
}

// ---------------------------------------------------------- позвать коллегу

/**
 * Заявка «позвать коллегу» (`docs/colleague-referral-design.md`, 3.3 и 4.1).
 * Ответ 204 и пустой: показывать после отправки нечего (решение 2), отсюда
 * `callVoid`.
 *
 * Имя и контакт уезжают СЫРЫМИ, как их набрал человек: нормализует сервер той
 * же функцией, что и форма, и в базу ложится его значение, а не наше.
 *
 * ⚠️ Ключа идемпотентности нет намеренно (4.1 проекта): дубль от повтора съест
 * одно место из пяти и будет закрыт админом. От двойного нажатия держит
 * выключенная на время запроса кнопка.
 */
export function createColleagueReferral(body: CreateColleagueReferralRequest): Promise<void> {
  return callVoid("/v1/colleague-referrals", { method: "POST", body });
}
